import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";

// ── CONFIG ───────────────────────────────────────────────────
const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function sbGet(path) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
  });
  if (!r.ok) return null;
  const d = await r.json();
  return Array.isArray(d) ? d[0] || null : null;
}

async function sbPost(path, body) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: "POST",
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      "Content-Type": "application/json",
      Prefer: "return=minimal",
    },
    body: JSON.stringify(body),
  });
  return r.ok;
}


// ── CONTENT HELPER ───────────────────────────────────────────
// Schema v2 menyimpan detail di wedding.content (jsonb).
// wc() baca flat column dulu, fallback ke content.key
// wcArr() sama tapi untuk array
function wc(wedding, key) {
  const flat = wedding[key];
  if (flat !== undefined && flat !== null && flat !== "") return flat;
  return wedding.content?.[key] ?? "";
}
function wcArr(wedding, key) {
  if (Array.isArray(wedding[key]) && wedding[key].length > 0) return wedding[key];
  return Array.isArray(wedding.content?.[key]) ? wedding.content[key] : [];
}

// ── HOOKS ────────────────────────────────────────────────────
function useCountdown(target) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = new Date(target) - new Date();
      if (diff <= 0) return setT({ d: 0, h: 0, m: 0, s: 0 });
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  return t;
}

function useReveal(threshold = 0.15) {
  const ref = useRef();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function useRsvp(wedding, guest) {
  const [rsvp, setRsvp] = useState("");
  const [msg, setMsg]   = useState("");
  const [sent, setSent]     = useState(false);
  const [sending, setSending] = useState(false);
  async function submit() {
    if (!rsvp || sending) return;
    setSending(true);
    const ok = await sbPost("rsvps", {
      wedding_id:  wedding.id,
      name:        guest?.name || "Tamu",
      attendance:  rsvp,
      note:        msg || null,
      guest_token: guest?.token || null,
    });
    if (ok) setSent(true);
    setSending(false);
  }
  return { rsvp, setRsvp, msg, setMsg, sent, sending, submit };
}

function useGuestbook(wedding) {
  const [name, setName] = useState("");
  const [msg, setMsg]   = useState("");
  const [sent, setSent] = useState(false);
  async function submit() {
    if (!msg.trim()) return;
    await sbPost("guestbook", {
      wedding_id: wedding.id,
      name:       name || "Anonim",
      message:    msg,
    });
    setSent(true);
  }
  return { name, setName, msg, setMsg, sent, submit };
}

// ── SHARED COMPONENTS ────────────────────────────────────────
function Rev({ children, d = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div
      ref={ref}
      style={{
        opacity:    vis ? 1 : 0,
        transform:  vis ? "none" : "translateY(28px)",
        transition: `opacity 0.9s ${d}s ease, transform 0.9s ${d}s ease`,
      }}
    >
      {children}
    </div>
  );
}

function LightboxModal({ photos, startIndex, onClose }) {
  const [i, setI] = useState(startIndex);
  return (
    <div
      onClick={onClose}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.97)", zIndex:500,
               display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
    >
      <button onClick={onClose}
        style={{ position:"absolute", top:18, right:22, fontSize:"1.2rem",
                 color:"rgba(255,255,255,0.4)", cursor:"pointer", background:"none", border:"none" }}>✕</button>
      <button
        onClick={e => { e.stopPropagation(); setI(j => (j - 1 + photos.length) % photos.length); }}
        style={{ position:"absolute", top:"50%", left:8, transform:"translateY(-50%)",
                 fontSize:"2rem", color:"rgba(255,255,255,0.3)", cursor:"pointer",
                 background:"none", border:"none", padding:20 }}>‹</button>
      <img src={photos[i]} alt="" onClick={e => e.stopPropagation()}
        style={{ maxWidth:"90vw", maxHeight:"88vh", objectFit:"contain" }} />
      <button
        onClick={e => { e.stopPropagation(); setI(j => (j + 1) % photos.length); }}
        style={{ position:"absolute", top:"50%", right:8, transform:"translateY(-50%)",
                 fontSize:"2rem", color:"rgba(255,255,255,0.3)", cursor:"pointer",
                 background:"none", border:"none", padding:20 }}>›</button>
    </div>
  );
}


// ── PUBLIC FEED HOOK ─────────────────────────────────────────
function usePublicFeed(weddingId) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!weddingId) return;
    async function load() {
      // Fetch guestbook + rsvp (yang ada catatan) paralel
      const [gbRes, rsvpRes] = await Promise.all([
        fetch(`${SUPABASE_URL}/rest/v1/guestbook?wedding_id=eq.${weddingId}&order=created_at.desc&limit=50`, {
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
        }),
        fetch(`${SUPABASE_URL}/rest/v1/rsvps?wedding_id=eq.${weddingId}&note=not.is.null&order=created_at.desc&limit=30`, {
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
        }),
      ]);
      const gb   = gbRes.ok   ? await gbRes.json()   : [];
      const rsvp = rsvpRes.ok ? await rsvpRes.json() : [];

      const feed = [
        ...gb.map(g => ({ id:"gb-"+g.id,   name: g.name,       text: g.message, type:"ucapan",   attendance: null })),
        ...rsvp.map(r => ({ id:"rv-"+r.id, name: r.name,       text: r.note,    type:"rsvp", attendance: r.attendance })),
      ].sort(() => Math.random() - 0.5); // acak urutan agar lebih natural

      setItems(feed);
    }
    load();
  }, [weddingId]);
  return items;
}

// ── LIVE FEED TICKER ─────────────────────────────────────────
// Infinite vertical scroll — items di-loop agar terkesan ramai
function LiveFeedTicker({ weddingId, accentColor = "#C9A84C", textColor = "rgba(240,232,216,0.75)", bgColor = "rgba(255,255,255,0.03)", borderColor = "rgba(255,255,255,0.07)" }) {
  const rawItems = usePublicFeed(weddingId);

  if (rawItems.length === 0) return null;

  // Duplikat minimal 12 item agar loop tidak terasa
  const MIN = 12;
  let items = [...rawItems];
  while (items.length < MIN) items = [...items, ...rawItems];

  // Render dua kali untuk seamless loop
  const doubled = [...items, ...items];

  const attendanceIcon = (a) => a === "hadir" ? "✓ Hadir" : a === "tidak_hadir" ? "✗ Tidak hadir" : a === "mungkin" ? "? Mungkin" : null;

  const tickerStyle = {
    overflow: "hidden",
    position: "relative",
    height: 260,
    maskImage: "linear-gradient(to bottom, transparent, black 14%, black 86%, transparent)",
    WebkitMaskImage: "linear-gradient(to bottom, transparent, black 14%, black 86%, transparent)",
  };

  const innerStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    animation: `livefeed ${items.length * 3.5}s linear infinite`,
    willChange: "transform",
  };

  return (
    <>
      <style>{`
        @keyframes livefeed {
          0%   { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
        .lft-ticker:hover .lft-inner { animation-play-state: paused; }
      `}</style>
      <div className="lft-ticker" style={tickerStyle}>
        <div className="lft-inner" style={innerStyle}>
          {doubled.map((item, i) => (
            <div key={i} style={{
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              padding: "10px 14px",
              background: bgColor,
              border: `1px solid ${borderColor}`,
              borderRadius: 2,
              flexShrink: 0,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: `${accentColor}18`,
                border: `1px solid ${accentColor}33`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: ".62rem", flexShrink: 0, color: accentColor,
              }}>
                {item.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3, flexWrap: "wrap" }}>
                  <span style={{ fontSize: ".42rem", letterSpacing: ".1em", color: accentColor, fontFamily: "sans-serif" }}>
                    {item.name}
                  </span>
                  {item.attendance && (
                    <span style={{ fontSize: ".35rem", letterSpacing: ".08em", color: `${accentColor}88`, padding: "1px 6px", border: `1px solid ${accentColor}30` }}>
                      {attendanceIcon(item.attendance)}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: ".44rem", color: textColor, lineHeight: 1.65, fontStyle: "italic", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                  "{item.text}"
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}


// ── MUSIC PLAYER ─────────────────────────────────────────────
function MusicPlayer({ url, accentColor = "#C9A84C", dark = true }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ready,   setReady]   = useState(false);
  const txColor = dark ? "rgba(240,232,216,.55)" : "rgba(28,24,16,.55)";
  const bg      = dark ? "rgba(255,255,255,.04)"  : "rgba(0,0,0,.04)";
  const border  = dark ? "rgba(255,255,255,.08)"  : "rgba(0,0,0,.08)";

  useEffect(() => {
    if (!url) return;
    const a = new Audio(url);
    a.loop = true;
    a.volume = 0.55;
    audioRef.current = a;
    a.addEventListener("canplaythrough", () => setReady(true));
    return () => { a.pause(); a.src = ""; };
  }, [url]);

  function toggle() {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().catch(()=>{}); setPlaying(true); }
  }

  if (!url) return null;
  return (
    <button
      onClick={toggle}
      title={playing ? "Jeda Musik" : "Putar Musik"}
      style={{
        position:"fixed", bottom:24, right:24, zIndex:200,
        width:44, height:44, borderRadius:"50%",
        background:bg, border:`1px solid ${border}`,
        backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)",
        cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:"1.1rem", transition:"all .3s", color: accentColor,
        boxShadow:`0 4px 20px rgba(0,0,0,.3), 0 0 0 1px ${accentColor}22`,
      }}
    >
      {ready ? (playing ? "⏸" : "▶") : "♩"}
    </button>
  );
}

// ── OPENING TEXT SECTION ──────────────────────────────────────
function OpeningSection({ text, accentColor, textColor, borderColor, bg, fontFamily = "serif" }) {
  if (!text) return null;
  return (
    <Rev>
      <div style={{
        padding:"52px 36px", textAlign:"center", maxWidth:600, margin:"0 auto",
        borderTop:`1px solid ${borderColor || "rgba(255,255,255,.06)"}`,
        borderBottom:`1px solid ${borderColor || "rgba(255,255,255,.06)"}`,
        background: bg || "transparent",
      }}>
        <div style={{ fontSize:".38rem", letterSpacing:".5em", color:accentColor, opacity:.55, marginBottom:18, fontFamily:"'Cinzel',serif" }}>
          ✦ BISMILLAH ✦
        </div>
        <p style={{
          fontFamily, fontSize:"clamp(.82rem,2vw,1rem)", lineHeight:2.1,
          color: textColor || "rgba(240,232,216,.5)", fontStyle:"italic",
          whiteSpace:"pre-line",
        }}>
          {text}
        </p>
      </div>
    </Rev>
  );
}

// ── LIVE STREAMING BUTTON ────────────────────────────────────
function LiveStreamButton({ url, accentColor, borderColor }) {
  if (!url) return null;
  return (
    <div style={{ textAlign:"center", marginTop:24 }}>
      <a href={url} target="_blank" rel="noopener noreferrer"
        style={{
          display:"inline-flex", alignItems:"center", gap:10,
          padding:"12px 28px", border:`1px solid ${borderColor || "rgba(255,255,255,.12)"}`,
          color: accentColor, fontFamily:"'Cinzel',serif", fontSize:".46rem",
          letterSpacing:".22em", textDecoration:"none", transition:"all .25s",
          background:"rgba(255,255,255,.02)",
        }}
        onMouseEnter={e=>{ e.currentTarget.style.background=`${accentColor}0f`; e.currentTarget.style.borderColor=`${accentColor}55`; }}
        onMouseLeave={e=>{ e.currentTarget.style.background="rgba(255,255,255,.02)"; e.currentTarget.style.borderColor=borderColor||"rgba(255,255,255,.12)"; }}
      >
        📺 Saksikan Live Streaming
      </a>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// TEMA 6 — BLOOM  (minimalist single column, white-green)
// ═══════════════════════════════════════════════════════════════
export function ThemeBloom({ wedding, guest }) {
  const rsvp   = useRsvp(wedding, guest);
  const gb     = useGuestbook(wedding);
  const [lb, setLb]         = useState(null);
  const [copied, setCopied] = useState("");
  const countdown = useCountdown(wc(wedding,'target_date') || wedding.wedding_date);
  const photos = wcArr(wedding,'gallery').length ? wcArr(wedding,'gallery') : wcArr(wedding,'photos');
  const banks  = wcArr(wedding,'banks').length ? wcArr(wedding,'banks') : wcArr(wedding,'bank_accounts');

  function copyNum(num, key) {
    navigator.clipboard.writeText(num.replace(/\s/g,""));
    setCopied(key); setTimeout(() => setCopied(""), 2000);
  }

  const C = `
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;1,400&family=Josefin+Sans:wght@200;300&display=swap');
    *{margin:0;padding:0;box-sizing:border-box;}
    :root{--bg:#FAFAF7;--bg2:#F2F5EF;--ac:#7A9E7E;--tx:rgba(28,46,30,0.9);--txm:rgba(28,46,30,0.38);--bdr:rgba(122,158,126,0.14);}
    body{background:var(--bg);color:var(--tx);font-family:'Josefin Sans',sans-serif;font-weight:300;overflow-x:hidden;}
    @keyframes blFadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
    @keyframes blGrow{from{transform:scaleY(0);transform-origin:top}to{transform:scaleY(1);transform-origin:top}}
    /* HERO — stark minimal */
    .bl-hero{min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:100px 28px;text-align:center;position:relative;overflow:hidden;}
    .bl-leaf-tl{position:absolute;top:-32px;left:-28px;font-size:clamp(7rem,22vw,14rem);opacity:.04;color:var(--ac);pointer-events:none;transform:rotate(-18deg);user-select:none;}
    .bl-leaf-br{position:absolute;bottom:-32px;right:-28px;font-size:clamp(7rem,22vw,14rem);opacity:.04;color:var(--ac);pointer-events:none;transform:rotate(162deg);user-select:none;}
    .bl-tag{font-size:12px;letter-spacing:.62em;color:rgba(122,158,126,.5);margin-bottom:38px;animation:blFadeUp 1s ease both;}
    .bl-guest{font-family:'Playfair Display',serif;font-style:italic;font-size:16px;color:rgba(122,158,126,.65);margin-bottom:14px;animation:blFadeUp 1s ease .1s both;}
    .bl-couple{font-family:'Playfair Display',serif;font-size:clamp(2.8rem,11.5vw,6rem);line-height:.87;animation:blFadeUp 1.2s ease .16s both;}
    .bl-bride{color:var(--ac);font-style:italic;}
    .bl-amp{display:block;font-family:'Josefin Sans',sans-serif;font-size:.18em;letter-spacing:.52em;opacity:.15;margin:13px 0;font-weight:200;}
    .bl-vline{width:1px;height:56px;background:linear-gradient(to bottom,rgba(122,158,126,.25),transparent);margin:30px auto;animation:blGrow 1s ease .38s both;}
    .bl-date{font-size:13px;letter-spacing:.38em;color:var(--txm);animation:blFadeUp 1s ease .42s both;}
    /* single narrow column */
    .bl-wrap{max-width:460px;margin:0 auto;padding:0 28px 100px;}
    .bl-section{padding:64px 0;border-bottom:1px solid rgba(122,158,126,.07);}
    .bl-section:last-child{border-bottom:none;}
    .bl-label{font-size:12px;letter-spacing:.62em;color:rgba(122,158,126,.48);text-align:center;margin-bottom:9px;}
    .bl-h2{font-family:'Playfair Display',serif;font-size:clamp(1.5rem,5vw,2.2rem);text-align:center;font-weight:400;margin-bottom:18px;}
    .bl-rule{width:22px;height:1px;background:rgba(122,158,126,.18);margin:0 auto 24px;}
    /* event */
    .bl-ev{padding:18px 0;border-bottom:1px solid rgba(122,158,126,.07);}
    .bl-ev:last-child{border-bottom:none;}
    .bl-ev-type{font-size:12px;letter-spacing:.52em;color:rgba(122,158,126,.58);margin-bottom:7px;}
    .bl-ev-date{font-family:'Playfair Display',serif;font-size:16px;font-style:italic;margin-bottom:2px;}
    .bl-ev-time{font-size:13px;letter-spacing:.2em;color:var(--txm);margin-bottom:5px;}
    .bl-ev-venue{font-size:17px;color:var(--txm);line-height:1.88;}
    /* countdown */
    .bl-cd{display:flex;justify-content:center;}
    .bl-cd-item{padding:12px 14px;text-align:center;}
    .bl-cd-num{font-family:'Playfair Display',serif;font-size:clamp(1.8rem,7vw,2.8rem);color:var(--ac);line-height:1;}
    .bl-cd-unit{font-size:11px;letter-spacing:.32em;color:var(--txm);margin-top:4px;}
    /* love */
    .bl-love{font-family:'Playfair Display',serif;font-size:16px;line-height:2.25;color:var(--txm);font-style:italic;white-space:pre-line;}
    /* gallery — single column stacked */
    .bl-gal{display:flex;flex-direction:column;gap:3px;}
    .bl-gal img{width:100%;aspect-ratio:4/3;object-fit:cover;cursor:pointer;filter:saturate(.82);transition:opacity .3s,filter .3s;}
    .bl-gal img:hover{opacity:.88;filter:saturate(1);}
    /* rsvp */
    .bl-rsvp-opts{display:flex;gap:6px;margin-bottom:12px;}
    .bl-ropt{flex:1;padding:11px 5px;border:1px solid var(--bdr);cursor:pointer;text-align:center;font-size:12px;letter-spacing:.18em;color:var(--txm);transition:all .25s;}
    .bl-ropt:hover,.bl-ropt.sel{border-color:var(--ac);color:var(--ac);background:rgba(122,158,126,.04);}
    .bl-inp{width:100%;padding:9px 0;background:transparent;border:none;border-bottom:1px solid var(--bdr);color:var(--tx);font-family:'Playfair Display',serif;font-size:16px;font-style:italic;outline:none;resize:none;transition:border-color .25s;margin-bottom:11px;}
    .bl-inp:focus{border-bottom-color:var(--ac);}
    .bl-inp::placeholder{color:var(--txm);}
    .bl-btn{padding:10px 26px;background:var(--ac);color:var(--bg);font-size:12px;letter-spacing:.3em;border:none;cursor:pointer;transition:opacity .2s;}
    .bl-btn:hover{opacity:.85;}.bl-btn:disabled{opacity:.4;cursor:default;}
    .bl-btn-ghost{padding:9px 20px;background:transparent;border:1px solid var(--bdr);color:var(--ac);font-size:12px;letter-spacing:.24em;cursor:pointer;transition:all .25s;}
    .bl-btn-ghost:hover{background:rgba(122,158,126,.04);}
    /* amplop */
    .bl-amp-item{padding:13px 0;border-bottom:1px solid rgba(122,158,126,.07);display:flex;justify-content:space-between;align-items:center;}
    .bl-amp-bank{font-size:12px;letter-spacing:.28em;color:rgba(122,158,126,.68);}
    .bl-amp-num{font-family:'Playfair Display',serif;font-size:16px;font-style:italic;margin:3px 0;}
    .bl-amp-holder{font-size:16px;color:var(--txm);}
    /* footer */
    .bl-footer{padding:30px;text-align:center;font-size:12px;letter-spacing:.42em;color:rgba(28,46,30,.18);}
    @media(max-width:480px){.bl-rsvp-opts{flex-direction:column;}}
  `;

  return (
    <>
      <style>{C}</style>

      {/* ── STARK MINIMAL HERO ── */}
      <section className="bl-hero">
        <div className="bl-leaf-tl">🌿</div>
        <div className="bl-leaf-br">🌿</div>
        <div className="bl-tag">✦ UNDANGAN PERNIKAHAN ✦</div>
        {guest && <div className="bl-guest">Kepada Yth. {guest.name}</div>}
        <div className="bl-couple">
          {wedding.groom_name}
          <span className="bl-amp">&amp;</span>
          <span className="bl-bride">{wedding.bride_name}</span>
        </div>
        <div className="bl-vline" />
        <div className="bl-date">{wc(wedding,'resepsi_date') || wc(wedding,'akad_date')}</div>
      </section>

      {/* ── KATA PEMBUKA ── */}
      {wc(wedding,'opening_text') && (
        <OpeningSection
          text={wc(wedding,'opening_text')}
          accentColor="#7A9E7E"
          textColor="rgba(28,46,30,.48)"
          borderColor="rgba(122,158,126,.12)"
          bg="rgba(242,245,239,.7)"
          fontFamily="'Playfair Display',serif"
        />
      )}

      {/* ── MUSIC PLAYER ── */}
      <MusicPlayer url={wc(wedding,'music_url')} accentColor="#7A9E7E" dark={false} />

      {/* ── SINGLE NARROW COLUMN ── */}
      <div className="bl-wrap">

        <Rev>
          <div className="bl-section">
            <div className="bl-label">Waktu &amp; Tempat</div>
            <div className="bl-h2">Hari Istimewa</div>
            <div className="bl-rule" />
            <div className="bl-ev">
              <div className="bl-ev-type">Akad Nikah</div>
              <div className="bl-ev-date">{wc(wedding,'akad_date')}</div>
              <div className="bl-ev-time">{wc(wedding,'akad_time')}</div>
              <div className="bl-ev-venue">{wc(wedding,'venue_name')} · {wc(wedding,'venue_address')}</div>
            </div>
            <div className="bl-ev">
              <div className="bl-ev-type">Resepsi</div>
              <div className="bl-ev-date">{wc(wedding,'resepsi_date')}</div>
              <div className="bl-ev-time">{wc(wedding,'resepsi_time')}</div>
              <div className="bl-ev-venue">{wc(wedding,'venue_name')} · {wc(wedding,'venue_address')}</div>
            </div>
            {wc(wedding,'venue_maps_url') && (
              <a href={wc(wedding,'venue_maps_url')} target="_blank" rel="noopener"
                style={{ display:"inline-block", marginTop:13, fontSize:".4rem",
                         letterSpacing:".26em", color:"var(--ac)", textDecoration:"none" }}>
                📍 Lihat Peta →
              </a>
            )}
            {wc(wedding,'dress_code') && (
              <div style={{ marginTop:12, fontSize:".4rem", letterSpacing:".24em", color:"var(--txm)" }}>
                Dress Code · {wc(wedding,'dress_code')}
              </div>
            )}
            <LiveStreamButton url={wc(wedding,'live_streaming_url')} accentColor="#7A9E7E" borderColor="rgba(122,158,126,.18)" />
          </div>
        </Rev>

        <Rev d={0.05}>
          <div className="bl-section">
            <div className="bl-label">Menghitung Hari</div>
            <div className="bl-cd">
              {[["d","Hari"],["h","Jam"],["m","Menit"],["s","Detik"]].map(([k,l]) => (
                <div key={k} className="bl-cd-item">
                  <div className="bl-cd-num">{String(countdown[k]).padStart(2,"0")}</div>
                  <div className="bl-cd-unit">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </Rev>

        {wc(wedding,'love_story') && (
          <Rev d={0.05}>
            <div className="bl-section">
              <div className="bl-label">Kisah Kami</div>
              <div className="bl-h2">Perjalanan Cinta</div>
              <div className="bl-rule" />
              <p className="bl-love">{wc(wedding,'love_story')}</p>
            </div>
          </Rev>
        )}

        {photos.length > 0 && (
          <Rev>
            <div className="bl-section">
              <div className="bl-label">Galeri Foto</div>
              <div className="bl-h2">Momen Berharga</div>
              <div className="bl-rule" />
              <div className="bl-gal">
                {photos.map((p,i) => <img key={i} src={p} alt="" onClick={() => setLb(i)} />)}
              </div>
            </div>
          </Rev>
        )}

        <Rev>
          <div className="bl-section">
            <div className="bl-label">Konfirmasi</div>
            <div className="bl-h2">RSVP</div>
            <div className="bl-rule" />
            {rsvp.sent ? (
              <div style={{ fontFamily:"'Playfair Display',serif", fontStyle:"italic",
                            color:"rgba(122,158,126,.68)" }}>✦ Terima kasih atas konfirmasimu.</div>
            ) : (
              <>
                <div className="bl-rsvp-opts">
                  {[["hadir","Hadir"],["mungkin","Mungkin"],["tidak_hadir","Tidak Hadir"]].map(([v,l]) => (
                    <div key={v} className={`bl-ropt${rsvp.rsvp===v?" sel":""}`}
                         onClick={() => rsvp.setRsvp(v)}>{l}</div>
                  ))}
                </div>
                <textarea className="bl-inp" rows={3} placeholder="Ucapan..."
                  value={rsvp.msg} onChange={e => rsvp.setMsg(e.target.value)} />
                <button className="bl-btn" onClick={rsvp.submit} disabled={!rsvp.rsvp}>
                  Kirim Konfirmasi
                </button>
              </>
            )}
          </div>
        </Rev>

        <Rev>
          <div className="bl-section">
            <div className="bl-label">Buku Tamu</div>
            <div className="bl-h2">Ucapan &amp; Doa</div>
            <div className="bl-rule" />
            <LiveFeedTicker weddingId={wedding.id} accentColor="#7A9E7E" textColor="rgba(28,46,30,0.65)" bgColor="rgba(122,158,126,0.04)" borderColor="rgba(122,158,126,0.1)" />
            {gb.sent ? (
              <div style={{ fontFamily:"'Playfair Display',serif", fontStyle:"italic",
                            color:"rgba(122,158,126,.58)" }}>✦ Ucapanmu sudah terkirim.</div>
            ) : (
              <>
                <input className="bl-inp" placeholder="Nama..."
                  value={gb.name} onChange={e => gb.setName(e.target.value)} />
                <textarea className="bl-inp" rows={3} placeholder="Tulis ucapan..."
                  value={gb.msg} onChange={e => gb.setMsg(e.target.value)} />
                <button className="bl-btn-ghost" onClick={gb.submit}>Kirim Ucapan</button>
              </>
            )}
          </div>
        </Rev>

        {banks.length > 0 && (
          <Rev>
            <div className="bl-section">
              <div className="bl-label">Amplop Digital</div>
              <div className="bl-h2">Hadiah &amp; Doa</div>
              <div className="bl-rule" />
              {banks.map((b,i) => (
                <div key={i} className="bl-amp-item">
                  <div>
                    <div className="bl-amp-bank">{b.bank||b.name}</div>
                    <div className="bl-amp-num">{b.number||b.account}</div>
                    <div className="bl-amp-holder">a.n. {b.holder}</div>
                  </div>
                  {copied===String(i)
                    ? <span style={{ fontSize:".4rem", color:"var(--ac)" }}>Disalin ✓</span>
                    : <button className="bl-btn-ghost" style={{ padding:"5px 12px" }}
                        onClick={() => copyNum(b.number||b.account, String(i))}>Salin</button>
                  }
                </div>
              ))}
            </div>
          </Rev>
        )}

      </div>{/* /bl-wrap */}

      <div className="bl-footer">✦ MYKISAH · Dibuat dengan cinta</div>

      {lb !== null && <LightboxModal photos={photos} startIndex={lb} onClose={() => setLb(null)} />}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// TEMA 7 — PRIYAYI  (Jawa keraton, batik pattern, coklat soga-emas)
// ═══════════════════════════════════════════════════════════════
export function ThemePriyayi({ wedding, guest }) {
  const rsvp   = useRsvp(wedding, guest);
  const gb     = useGuestbook(wedding);
  const [lb, setLb]         = useState(null);
  const [copied, setCopied] = useState("");
  const countdown = useCountdown(wc(wedding,'target_date') || wedding.wedding_date);
  const photos = wcArr(wedding,'gallery').length ? wcArr(wedding,'gallery') : wcArr(wedding,'photos');
  const banks  = wcArr(wedding,'banks').length ? wcArr(wedding,'banks') : wcArr(wedding,'bank_accounts');

  function copyNum(num, key) {
    navigator.clipboard.writeText(num.replace(/\s/g,""));
    setCopied(key); setTimeout(() => setCopied(""), 2000);
  }

  const C = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;1,400&family=Cinzel:wght@400;500&display=swap');
    *{margin:0;padding:0;box-sizing:border-box;}
    :root{--bg:#1A1105;--bg2:#140E04;--ac:#C9A84C;--tx:rgba(232,213,163,0.9);--txm:rgba(232,213,163,0.38);--bdr:rgba(201,168,76,0.13);}
    body{background:var(--bg);color:var(--tx);font-family:'Cormorant Garamond',serif;overflow-x:hidden;}
    @keyframes prFadeUp{from{opacity:0;transform:translateY(26px)}to{opacity:1;transform:translateY(0)}}
    /* batik texture overlay */
    body::before{
      content:'';position:fixed;inset:0;pointer-events:none;z-index:0;
      background-image:
        repeating-linear-gradient(45deg, rgba(201,168,76,.018) 0px, rgba(201,168,76,.018) 1px, transparent 1px, transparent 9px),
        repeating-linear-gradient(-45deg, rgba(201,168,76,.018) 0px, rgba(201,168,76,.018) 1px, transparent 1px, transparent 9px);
    }
    /* gunungan wayang divider SVG-ish via clip */
    .pr-gunungan{text-align:center;font-size:2rem;color:rgba(201,168,76,.2);margin:8px 0;line-height:1;}
    /* HERO — keraton frame */
    .pr-hero{min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:80px 44px;position:relative;z-index:1;}
    .pr-frame-outer{position:absolute;inset:20px;border:1px solid rgba(201,168,76,.16);pointer-events:none;}
    .pr-frame-inner{position:absolute;inset:30px;border:1px solid rgba(201,168,76,.07);pointer-events:none;}
    .pr-corner{position:absolute;width:22px;height:22px;border-color:rgba(201,168,76,.38);border-style:solid;}
    .pr-tl{top:20px;left:20px;border-width:1px 0 0 1px;}
    .pr-tr{top:20px;right:20px;border-width:1px 1px 0 0;}
    .pr-bl{bottom:20px;left:20px;border-width:0 0 1px 1px;}
    .pr-br{bottom:20px;right:20px;border-width:0 1px 1px 0;}
    .pr-bismillah{font-family:'Cormorant Garamond',serif;font-size:1.55rem;font-style:italic;color:rgba(201,168,76,.35);margin-bottom:18px;animation:prFadeUp 1s ease both;}
    .pr-tag{font-family:'Cinzel',serif;font-size:12px;letter-spacing:.56em;color:rgba(201,168,76,.52);margin-bottom:26px;animation:prFadeUp 1s ease .1s both;}
    .pr-guest{font-family:'Cinzel',serif;font-size:13px;letter-spacing:.22em;color:rgba(201,168,76,.44);margin-bottom:9px;animation:prFadeUp 1s ease .15s both;}
    .pr-couple{font-family:'Cinzel',serif;font-size:clamp(2.4rem,8.5vw,5rem);line-height:.87;letter-spacing:.04em;animation:prFadeUp 1.2s ease .2s both;}
    .pr-bride{color:var(--ac);}
    .pr-amp{display:block;font-family:'Cormorant Garamond',serif;font-size:.19em;font-style:italic;opacity:.17;margin:12px 0;letter-spacing:.22em;}
    .pr-ornrule{display:flex;align-items:center;gap:12px;justify-content:center;margin:20px 0;animation:prFadeUp 1s ease .32s both;}
    .pr-ornrule::before,.pr-ornrule::after{content:'';flex:1;max-width:56px;height:1px;background:rgba(201,168,76,.14);}
    .pr-ornrule span{color:rgba(201,168,76,.38);font-size:17px;}
    .pr-fullname{font-size:17px;color:rgba(232,213,163,.38);line-height:2.1;animation:prFadeUp 1s ease .36s both;}
    .pr-date{font-family:'Cinzel',serif;font-size:13px;letter-spacing:.26em;color:var(--txm);margin-top:18px;animation:prFadeUp 1s ease .42s both;}
    /* sections */
    .pr-sec{padding:82px 44px;position:relative;z-index:1;}
    .pr-sec-alt{padding:82px 44px;background:rgba(0,0,0,.22);position:relative;z-index:1;}
    .pr-inner{max-width:660px;margin:0 auto;text-align:center;}
    .pr-kicker{font-family:'Cinzel',serif;font-size:12px;letter-spacing:.52em;color:rgba(201,168,76,.54);margin-bottom:9px;}
    .pr-h2{font-family:'Cinzel',serif;font-size:clamp(1.55rem,5vw,2.35rem);letter-spacing:.04em;margin-bottom:14px;line-height:1.1;}
    .pr-orndiv{display:flex;align-items:center;gap:11px;justify-content:center;margin-bottom:26px;}
    .pr-orndiv::before,.pr-orndiv::after{content:'';flex:1;max-width:52px;height:1px;background:rgba(201,168,76,.11);}
    .pr-orndiv span{color:rgba(201,168,76,.34);font-size:17px;}
    /* event cards — centered stacked */
    .pr-ev{padding:22px;border:1px solid var(--bdr);border-top:2px solid rgba(201,168,76,.28);margin-bottom:8px;background:rgba(201,168,76,.025);text-align:center;}
    .pr-ev-type{font-family:'Cinzel',serif;font-size:12px;letter-spacing:.42em;color:var(--ac);margin-bottom:10px;}
    .pr-ev-date{font-size:16px;font-style:italic;margin-bottom:3px;}
    .pr-ev-time{font-family:'Cinzel',serif;font-size:13px;letter-spacing:.16em;color:var(--txm);margin-bottom:9px;}
    .pr-ev-venue{font-size:17px;color:var(--txm);line-height:1.88;}
    /* countdown */
    .pr-cd{display:flex;border:1px solid var(--bdr);}
    .pr-cd-item{flex:1;text-align:center;border-right:1px solid var(--bdr);padding:18px 6px;}
    .pr-cd-item:last-child{border-right:none;}
    .pr-cd-num{font-family:'Cinzel',serif;font-size:clamp(1.9rem,7vw,3rem);color:var(--ac);line-height:1;}
    .pr-cd-unit{font-family:'Cinzel',serif;font-size:12px;letter-spacing:.28em;color:var(--txm);margin-top:5px;}
    /* love */
    .pr-love{font-size:16px;line-height:2.25;color:var(--txm);font-style:italic;white-space:pre-line;}
    /* gallery */
    .pr-gal{display:grid;grid-template-columns:1fr 1fr;gap:4px;text-align:left;}
    .pr-gal img{width:100%;aspect-ratio:4/3;object-fit:cover;cursor:pointer;filter:sepia(.18) brightness(.78);transition:filter .4s;}
    .pr-gal img:hover{filter:sepia(.08) brightness(.9);}
    /* rsvp */
    .pr-rsvp-opts{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:13px;}
    .pr-ropt{padding:14px 7px;border:1px solid var(--bdr);cursor:pointer;text-align:center;font-family:'Cinzel',serif;font-size:12px;letter-spacing:.13em;transition:all .25s;}
    .pr-ropt:hover,.pr-ropt.sel{border-color:var(--ac);background:rgba(201,168,76,.05);color:var(--ac);}
    .pr-ricon{font-size:1rem;display:block;margin-bottom:5px;}
    .pr-inp{width:100%;padding:10px 12px;background:rgba(201,168,76,.03);border:1px solid var(--bdr);color:var(--tx);font-family:'Cormorant Garamond',serif;font-size:16px;font-style:italic;outline:none;resize:none;transition:border-color .25s;margin-bottom:9px;}
    .pr-inp:focus{border-color:var(--ac);}
    .pr-inp::placeholder{color:var(--txm);}
    .pr-btn{padding:12px 28px;background:var(--ac);color:var(--bg);font-family:'Cinzel',serif;font-size:13px;letter-spacing:.24em;border:none;cursor:pointer;transition:opacity .2s;}
    .pr-btn:hover{opacity:.84;}.pr-btn:disabled{opacity:.4;cursor:default;}
    .pr-btn-ghost{padding:10px 20px;background:transparent;border:1px solid var(--bdr);color:var(--ac);font-family:'Cinzel',serif;font-size:12px;letter-spacing:.2em;cursor:pointer;transition:all .25s;}
    .pr-btn-ghost:hover{background:rgba(201,168,76,.04);}
    /* amplop */
    .pr-amp-card{padding:17px;background:rgba(201,168,76,.03);border:1px solid var(--bdr);display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;}
    .pr-amp-bank{font-family:'Cinzel',serif;font-size:12px;letter-spacing:.2em;color:var(--ac);}
    .pr-amp-num{font-size:16px;margin:4px 0;font-style:italic;}
    .pr-amp-holder{font-size:16px;color:var(--txm);}
    /* footer */
    .pr-footer{padding:34px;text-align:center;border-top:1px solid var(--bdr);position:relative;z-index:1;}
    @media(max-width:540px){.pr-rsvp-opts{grid-template-columns:1fr;}.pr-gal{grid-template-columns:1fr;}}
  `;

  return (
    <>
      <style>{C}</style>

      {/* ── KERATON HERO ── */}
      <section className="pr-hero">
        <div className="pr-frame-outer" /><div className="pr-frame-inner" />
        <div className="pr-corner pr-tl" /><div className="pr-corner pr-tr" />
        <div className="pr-corner pr-bl" /><div className="pr-corner pr-br" />
        <div className="pr-bismillah">بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيم</div>
        <div className="pr-tag">✦ UNDANGAN PERNIKAHAN ✦</div>
        {guest && <div className="pr-guest">Kepada Yth. · {guest.name}</div>}
        <div className="pr-couple">
          {wedding.groom_name}
          <span className="pr-amp">&amp;</span>
          <span className="pr-bride">{wedding.bride_name}</span>
        </div>
        <div className="pr-ornrule"><span>✾</span></div>
        <div className="pr-fullname">
          {wc(wedding,'groom_full') || wedding.groom_name}<br />
          <span style={{ opacity:.4 }}>&amp;</span><br />
          {wc(wedding,'bride_full') || wedding.bride_name}
        </div>
        <div className="pr-date">{wc(wedding,'resepsi_date') || wc(wedding,'akad_date')}</div>
      </section>

      {/* ── KATA PEMBUKA ── */}
      {wc(wedding,'opening_text') && (
        <OpeningSection text={wc(wedding,'opening_text')} accentColor="#C9A84C" textColor="rgba(240,232,216,.45)" borderColor="rgba(201,168,76,.1)" fontFamily="'Cormorant Garamond',serif" />
      )}
      <MusicPlayer url={wc(wedding,'music_url')} accentColor="#C9A84C" dark={true} />

      {/* ── EVENTS ── */}
      <section className="pr-sec">
        <div className="pr-inner">
          <Rev>
            <div className="pr-kicker">Waktu &amp; Tempat</div>
            <div className="pr-h2">Hari Istimewa</div>
            <div className="pr-orndiv"><span>✾</span></div>
            <div className="pr-ev">
              <div className="pr-ev-type">Akad Nikah</div>
              <div className="pr-ev-date">{wc(wedding,'akad_date')}</div>
              <div className="pr-ev-time">{wc(wedding,'akad_time')}</div>
              <div className="pr-ev-venue">{wc(wedding,'venue_name')}<br />{wc(wedding,'venue_address')}</div>
            </div>
            <div className="pr-ev">
              <div className="pr-ev-type">Resepsi</div>
              <div className="pr-ev-date">{wc(wedding,'resepsi_date')}</div>
              <div className="pr-ev-time">{wc(wedding,'resepsi_time')}</div>
              <div className="pr-ev-venue">{wc(wedding,'venue_name')}<br />{wc(wedding,'venue_address')}</div>
            </div>
            {wc(wedding,'venue_maps_url') && (
              <div style={{ marginTop:14 }}>
                <a href={wc(wedding,'venue_maps_url')} target="_blank" rel="noopener"
                  style={{ fontFamily:"'Cinzel',serif", fontSize:".43rem",
                           letterSpacing:".2em", color:"var(--ac)", textDecoration:"none" }}>
                  📍 Peta Lokasi
                </a>
              </div>
            )}
            {wc(wedding,'dress_code') && (
              <div style={{ marginTop:18, fontFamily:"'Cinzel',serif", fontSize:".43rem",
                            letterSpacing:".2em", color:"var(--txm)" }}>
                Dress Code · {wc(wedding,'dress_code')}
              </div>
            )}
            <LiveStreamButton url={wc(wedding,'live_streaming_url')} accentColor="#C9A84C" borderColor="rgba(201,168,76,.2)" />
          </Rev>
        </div>
      </section>

      {/* ── COUNTDOWN ── */}
      <section className="pr-sec-alt">
        <div className="pr-inner">
          <Rev>
            <div className="pr-kicker">Menghitung Hari</div>
            <div className="pr-orndiv"><span>✾</span></div>
            <div className="pr-cd">
              {[["d","Hari"],["h","Jam"],["m","Menit"],["s","Detik"]].map(([k,l]) => (
                <div key={k} className="pr-cd-item">
                  <div className="pr-cd-num">{String(countdown[k]).padStart(2,"0")}</div>
                  <div className="pr-cd-unit">{l}</div>
                </div>
              ))}
            </div>
          </Rev>
        </div>
      </section>

      {/* ── LOVE STORY ── */}
      {wc(wedding,'love_story') && (
        <section className="pr-sec">
          <div className="pr-inner">
            <Rev>
              <div className="pr-kicker">Kisah Kami</div>
              <div className="pr-h2">Perjalanan Cinta</div>
              <div className="pr-orndiv"><span>✾</span></div>
              <p className="pr-love">{wc(wedding,'love_story')}</p>
            </Rev>
          </div>
        </section>
      )}

      {/* ── GALLERY ── */}
      {photos.length > 0 && (
        <section className="pr-sec-alt">
          <div className="pr-inner">
            <Rev>
              <div className="pr-kicker">Galeri Foto</div>
              <div className="pr-h2">Momen Berharga</div>
              <div className="pr-orndiv"><span>✾</span></div>
              <div className="pr-gal">
                {photos.map((p,i) => <img key={i} src={p} alt="" onClick={() => setLb(i)} />)}
              </div>
            </Rev>
          </div>
        </section>
      )}

      {/* ── RSVP ── */}
      <section className="pr-sec">
        <div className="pr-inner">
          <Rev>
            <div className="pr-kicker">Konfirmasi Kehadiran</div>
            <div className="pr-h2">RSVP</div>
            <div className="pr-orndiv"><span>✾</span></div>
            {rsvp.sent ? (
              <div style={{ fontStyle:"italic", color:"rgba(201,168,76,.65)" }}>✦ Terima kasih atas konfirmasimu.</div>
            ) : (
              <>
                <div className="pr-rsvp-opts">
                  {[["hadir","✅","Hadir"],["mungkin","🤔","Mungkin"],["tidak_hadir","❌","Tidak Hadir"]].map(([v,ic,l]) => (
                    <div key={v} className={`pr-ropt${rsvp.rsvp===v?" sel":""}`} onClick={() => rsvp.setRsvp(v)}>
                      <span className="pr-ricon">{ic}</span>{l}
                    </div>
                  ))}
                </div>
                <textarea className="pr-inp" rows={3} placeholder="Ucapan..."
                  value={rsvp.msg} onChange={e => rsvp.setMsg(e.target.value)} />
                <button className="pr-btn" onClick={rsvp.submit} disabled={!rsvp.rsvp}>
                  ✦ Kirim Konfirmasi
                </button>
              </>
            )}
          </Rev>
        </div>
      </section>

      {/* ── GUESTBOOK ── */}
      <section className="pr-sec-alt">
        <div className="pr-inner">
          <Rev>
            <div className="pr-kicker">Buku Tamu</div>
            <div className="pr-h2">Ucapan &amp; Doa</div>
            <div className="pr-orndiv"><span>✾</span></div>
            <LiveFeedTicker weddingId={wedding.id} accentColor="#C9A84C" textColor="rgba(240,232,216,0.65)" bgColor="rgba(201,168,76,0.04)" borderColor="rgba(201,168,76,0.1)" />
            {gb.sent ? (
              <div style={{ fontStyle:"italic", color:"rgba(201,168,76,.55)" }}>✦ Ucapanmu sudah terkirim.</div>
            ) : (
              <>
                <input className="pr-inp" placeholder="Nama..."
                  value={gb.name} onChange={e => gb.setName(e.target.value)} />
                <textarea className="pr-inp" rows={3} placeholder="Tulis ucapan..."
                  value={gb.msg} onChange={e => gb.setMsg(e.target.value)} />
                <button className="pr-btn-ghost" onClick={gb.submit}>✦ Kirim Ucapan</button>
              </>
            )}
          </Rev>
        </div>
      </section>

      {/* ── AMPLOP ── */}
      {banks.length > 0 && (
        <section className="pr-sec">
          <div className="pr-inner">
            <Rev>
              <div className="pr-kicker">Amplop Digital</div>
              <div className="pr-h2">Hadiah &amp; Doa</div>
              <div className="pr-orndiv"><span>✾</span></div>
              <div style={{ textAlign:"left" }}>
                {banks.map((b,i) => (
                  <div key={i} className="pr-amp-card">
                    <div>
                      <div className="pr-amp-bank">{b.bank||b.name}</div>
                      <div className="pr-amp-num">{b.number||b.account}</div>
                      <div className="pr-amp-holder">a.n. {b.holder}</div>
                    </div>
                    {copied===String(i)
                      ? <span style={{ fontFamily:"'Cinzel',serif", fontSize:".43rem", color:"var(--ac)" }}>Disalin ✓</span>
                      : <button className="pr-btn-ghost" style={{ padding:"6px 14px" }}
                          onClick={() => copyNum(b.number||b.account, String(i))}>Salin</button>
                    }
                  </div>
                ))}
              </div>
            </Rev>
          </div>
        </section>
      )}

      <div className="pr-footer">
        <div style={{ fontFamily:"'Cinzel',serif", fontSize:".49rem", letterSpacing:".42em", color:"rgba(201,168,76,.24)" }}>✦ MYKISAH ✦</div>
        <div style={{ fontSize:".55rem", color:"rgba(232,213,163,.14)", marginTop:4 }}>Dibuat dengan cinta</div>
      </div>

      {lb !== null && <LightboxModal photos={photos} startIndex={lb} onClose={() => setLb(null)} />}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// TEMA 8 — PASUNDAN  (Sunda taman, hijau-emas, organic layout)
// ═══════════════════════════════════════════════════════════════
export function ThemePasundan({ wedding, guest }) {
  const rsvp   = useRsvp(wedding, guest);
  const gb     = useGuestbook(wedding);
  const [lb, setLb]         = useState(null);
  const [copied, setCopied] = useState("");
  const countdown = useCountdown(wc(wedding,'target_date') || wedding.wedding_date);
  const photos = wcArr(wedding,'gallery').length ? wcArr(wedding,'gallery') : wcArr(wedding,'photos');
  const banks  = wcArr(wedding,'banks').length ? wcArr(wedding,'banks') : wcArr(wedding,'bank_accounts');

  function copyNum(num, key) {
    navigator.clipboard.writeText(num.replace(/\s/g,""));
    setCopied(key); setTimeout(() => setCopied(""), 2000);
  }

  /* extract day/month from date string like "Sabtu, 12 Juli 2025" */
  function parseDate(str) {
    if (!str) return { day:"—", month:"—" };
    const MAP = { januari:"JAN",februari:"FEB",maret:"MAR",april:"APR",mei:"MEI",juni:"JUN",juli:"JUL",agustus:"AGU",september:"SEP",oktober:"OKT",november:"NOV",desember:"DES" };
    const parts = str.toLowerCase().split(/\s+/);
    const day   = parts.find(p => /^\d+$/.test(p)) || "—";
    const mkey  = parts.find(p => MAP[p]);
    return { day, month: mkey ? MAP[mkey] : "—" };
  }

  const akad   = parseDate(wc(wedding,'akad_date'));
  const resepsi = parseDate(wc(wedding,'resepsi_date'));

  const C = `
    @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;1,400&family=Josefin+Sans:wght@200;300;400&display=swap');
    *{margin:0;padding:0;box-sizing:border-box;}
    :root{--bg:#F7F5EE;--bg2:#EFF5EE;--ac:#5A7A56;--acl:#7A9E76;--tx:rgba(32,48,30,0.9);--txm:rgba(32,48,30,0.4);--bdr:rgba(90,122,86,0.13);}
    body{background:var(--bg);color:var(--tx);font-family:'EB Garamond',serif;overflow-x:hidden;}
    @keyframes suFadeUp{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}}
    @keyframes suBloom{from{opacity:0;transform:scale(.82) rotate(-14deg)}to{opacity:1;transform:scale(1) rotate(0deg)}}
    /* HERO — garden with flora accents */
    .su-hero{min-height:100vh;display:flex;flex-direction:column;background:var(--bg);position:relative;overflow:hidden;}
    .su-flora-tl{position:absolute;top:-28px;left:-22px;font-size:clamp(7rem,20vw,13rem);opacity:.055;color:var(--ac);transform:rotate(-16deg);pointer-events:none;animation:suBloom 1.6s ease both;}
    .su-flora-br{position:absolute;bottom:-28px;right:-22px;font-size:clamp(7rem,20vw,13rem);opacity:.055;color:var(--ac);transform:rotate(164deg);pointer-events:none;animation:suBloom 1.6s ease .2s both;}
    .su-flora-tr{position:absolute;top:8%;right:4%;font-size:clamp(4rem,12vw,8rem);opacity:.035;color:var(--ac);transform:rotate(22deg);pointer-events:none;}
    .su-hero-content{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:100px 40px 40px;position:relative;z-index:2;}
    .su-kembang{font-size:1.45rem;color:rgba(90,122,86,.28);margin-bottom:22px;animation:suFadeUp 1s ease both;}
    .su-tag{font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.6em;color:rgba(90,122,86,.52);margin-bottom:26px;animation:suFadeUp 1s ease .1s both;font-weight:200;}
    .su-guest{font-family:'Josefin Sans',sans-serif;font-size:13px;letter-spacing:.22em;color:rgba(90,122,86,.5);margin-bottom:9px;animation:suFadeUp 1s ease .15s both;font-weight:200;}
    .su-couple{font-family:'EB Garamond',serif;font-size:clamp(2.8rem,10.5vw,6rem);line-height:.87;font-style:italic;animation:suFadeUp 1.2s ease .2s both;}
    .su-bride{color:var(--ac);}
    .su-amp{display:block;font-family:'Josefin Sans',sans-serif;font-size:.17em;letter-spacing:.52em;opacity:.14;margin:12px 0;font-style:normal;font-weight:200;}
    .su-tagline{font-family:'Josefin Sans',sans-serif;font-size:13px;letter-spacing:.22em;color:rgba(32,48,30,.32);margin-top:22px;font-weight:200;animation:suFadeUp 1s ease .38s both;}
    /* hero band */
    .su-band{background:var(--ac);padding:16px 40px;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:10px;position:relative;z-index:2;}
    .su-band-item{font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.22em;color:rgba(255,255,255,.6);text-align:center;font-weight:200;}
    .su-band-item strong{display:block;color:rgba(255,255,255,.9);font-weight:300;margin-bottom:2px;font-size:13px;}
    /* sections */
    .su-sec{padding:78px 40px;}
    .su-sec-alt{padding:78px 40px;background:var(--bg2);}
    .su-inner{max-width:660px;margin:0 auto;}
    .su-kicker{font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.6em;color:rgba(90,122,86,.58);text-align:center;margin-bottom:9px;font-weight:200;}
    .su-h2{font-family:'EB Garamond',serif;font-size:clamp(1.6rem,5vw,2.4rem);text-align:center;font-style:italic;margin-bottom:14px;}
    .su-rule{display:flex;align-items:center;gap:10px;justify-content:center;margin-bottom:24px;}
    .su-rule::before,.su-rule::after{content:'';flex:1;max-width:48px;height:1px;background:rgba(90,122,86,.13);}
    .su-rule span{color:rgba(90,122,86,.32);font-size:17px;}
    /* event — date column layout */
    .su-ev{border:1px solid var(--bdr);margin-bottom:9px;display:grid;grid-template-columns:76px 1fr;overflow:hidden;}
    .su-ev-date-col{background:var(--ac);display:flex;flex-direction:column;align-items:center;justify-content:center;padding:16px 8px;}
    .su-ev-day{font-family:'EB Garamond',serif;font-size:2rem;color:rgba(255,255,255,.9);line-height:1;font-style:italic;}
    .su-ev-month{font-family:'Josefin Sans',sans-serif;font-size:11px;letter-spacing:.3em;color:rgba(255,255,255,.5);margin-top:3px;font-weight:200;}
    .su-ev-body{padding:14px 18px;}
    .su-ev-type{font-family:'Josefin Sans',sans-serif;font-size:11px;letter-spacing:.4em;color:var(--ac);margin-bottom:6px;font-weight:300;}
    .su-ev-time{font-family:'EB Garamond',serif;font-size:16px;font-style:italic;margin-bottom:3px;}
    .su-ev-venue{font-size:17px;color:var(--txm);line-height:1.78;}
    /* countdown */
    .su-cd{display:flex;border:1px solid var(--bdr);}
    .su-cd-item{flex:1;text-align:center;border-right:1px solid var(--bdr);padding:16px 7px;}
    .su-cd-item:last-child{border-right:none;}
    .su-cd-num{font-family:'EB Garamond',serif;font-size:clamp(1.8rem,6.5vw,2.8rem);color:var(--ac);font-style:italic;line-height:1;}
    .su-cd-unit{font-family:'Josefin Sans',sans-serif;font-size:11px;letter-spacing:.28em;color:var(--txm);margin-top:5px;font-weight:200;}
    /* love */
    .su-love{font-size:16px;line-height:2.2;color:var(--txm);font-style:italic;white-space:pre-line;}
    /* gallery — wide first then grid */
    .su-gal{display:grid;grid-template-columns:1fr 1fr;gap:3px;}
    .su-gal-wide{grid-column:span 2;aspect-ratio:16/7;overflow:hidden;}
    .su-gal-sm{aspect-ratio:4/3;overflow:hidden;}
    .su-gal img{width:100%;height:100%;object-fit:cover;cursor:pointer;filter:saturate(.78);transition:filter .4s,transform .4s;}
    .su-gal img:hover{filter:saturate(1);transform:scale(1.02);}
    /* rsvp */
    .su-rsvp-opts{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:12px;}
    .su-ropt{padding:13px 7px;border:1px solid var(--bdr);cursor:pointer;text-align:center;font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.16em;color:var(--txm);transition:all .25s;font-weight:300;}
    .su-ropt:hover,.su-ropt.sel{border-color:var(--ac);color:var(--ac);background:rgba(90,122,86,.04);}
    .su-inp{width:100%;padding:10px 12px;background:rgba(90,122,86,.03);border:1px solid var(--bdr);color:var(--tx);font-family:'EB Garamond',serif;font-size:16px;font-style:italic;outline:none;resize:none;transition:border-color .25s;margin-bottom:9px;}
    .su-inp:focus{border-color:var(--ac);}
    .su-inp::placeholder{color:var(--txm);}
    .su-btn{padding:11px 26px;background:var(--ac);color:var(--bg);font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.26em;border:none;cursor:pointer;transition:opacity .2s;font-weight:300;}
    .su-btn:hover{opacity:.85;}.su-btn:disabled{opacity:.4;cursor:default;}
    .su-btn-ghost{padding:10px 20px;background:transparent;border:1px solid var(--bdr);color:var(--ac);font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.22em;cursor:pointer;transition:all .25s;font-weight:300;}
    .su-btn-ghost:hover{background:rgba(90,122,86,.04);}
    /* amplop */
    .su-amp-row{padding:13px 0;border-bottom:1px solid var(--bdr);display:flex;justify-content:space-between;align-items:center;}
    .su-amp-bank{font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.22em;color:var(--ac);font-weight:300;}
    .su-amp-num{font-family:'EB Garamond',serif;font-size:16px;font-style:italic;margin:3px 0;}
    .su-amp-holder{font-size:16px;color:var(--txm);}
    /* footer */
    .su-footer{padding:34px;background:var(--ac);text-align:center;}
    @media(max-width:560px){
      .su-ev{grid-template-columns:1fr;}.su-ev-date-col{flex-direction:row;gap:8px;padding:10px 14px;justify-content:flex-start;}.su-band{justify-content:center;}.su-gal{grid-template-columns:1fr;}.su-gal-wide{grid-column:span 1;aspect-ratio:4/3;}.su-rsvp-opts{grid-template-columns:1fr;}
    }
  `;

  return (
    <>
      <style>{C}</style>

      {/* ── GARDEN HERO ── */}
      <div className="su-hero">
        <div className="su-flora-tl">🌿</div>
        <div className="su-flora-br">🌿</div>
        <div className="su-flora-tr">🌸</div>
        <div className="su-hero-content">
          <div className="su-kembang">🌸 🌿 🌸</div>
          <div className="su-tag">✦ UNDANGAN PERNIKAHAN SUNDA ✦</div>
          {guest && <div className="su-guest">Kasadana · {guest.name}</div>}
          <div className="su-couple">
            {wedding.groom_name}
            <span className="su-amp">&amp;</span>
            <span className="su-bride">{wedding.bride_name}</span>
          </div>
          <div className="su-tagline">Wilujeng Sumping · Selamat Datang</div>
        </div>
        <div className="su-band">
          {[
            ["Tanggal", wc(wedding,'resepsi_date') || wc(wedding,'akad_date')],
            ["Lokasi",  wc(wedding,'venue_name')],
            guest ? ["Tamu", guest.name] : ["Tema","Pasundan"],
          ].map(([k,v]) => (
            <div key={k} className="su-band-item"><strong>{v}</strong>{k}</div>
          ))}
        </div>
      </div>

      {/* ── KATA PEMBUKA ── */}
      {wc(wedding,'opening_text') && (
        <OpeningSection text={wc(wedding,'opening_text')} accentColor="#5A7A56" textColor="rgba(28,46,30,.48)" borderColor="rgba(90,122,86,.12)" bg="rgba(239,245,238,.8)" fontFamily="'EB Garamond',serif" />
      )}
      <MusicPlayer url={wc(wedding,'music_url')} accentColor="#5A7A56" dark={false} />

      {/* ── EVENTS ── */}
      <section className="su-sec">
        <div className="su-inner">
          <Rev>
            <div className="su-kicker">Waktu &amp; Tempat</div>
            <div className="su-h2">Dinten Istimewa</div>
            <div className="su-rule"><span>🌸</span></div>
            <div className="su-ev">
              <div className="su-ev-date-col">
                <div className="su-ev-day">{akad.day}</div>
                <div className="su-ev-month">{akad.month}</div>
              </div>
              <div className="su-ev-body">
                <div className="su-ev-type">Akad Nikah</div>
                <div className="su-ev-time">{wc(wedding,'akad_time')}</div>
                <div className="su-ev-venue">{wc(wedding,'venue_name')} · {wc(wedding,'venue_address')}</div>
              </div>
            </div>
            <div className="su-ev">
              <div className="su-ev-date-col">
                <div className="su-ev-day">{resepsi.day}</div>
                <div className="su-ev-month">{resepsi.month}</div>
              </div>
              <div className="su-ev-body">
                <div className="su-ev-type">Resepsi</div>
                <div className="su-ev-time">{wc(wedding,'resepsi_time')}</div>
                <div className="su-ev-venue">{wc(wedding,'venue_name')} · {wc(wedding,'venue_address')}</div>
              </div>
            </div>
            {wc(wedding,'venue_maps_url') && (
              <a href={wc(wedding,'venue_maps_url')} target="_blank" rel="noopener"
                style={{ display:"inline-block", marginTop:12, fontFamily:"'Josefin Sans',sans-serif",
                         fontSize:".4rem", letterSpacing:".22em", color:"var(--ac)",
                         textDecoration:"none", fontWeight:300 }}>
                📍 Peta Lokasi →
              </a>
            )}
            {wc(wedding,'dress_code') && (
              <div style={{ marginTop:12, fontFamily:"'Josefin Sans',sans-serif",
                            fontSize:".4rem", letterSpacing:".22em", color:"var(--txm)", fontWeight:200 }}>
                Dress Code · {wc(wedding,'dress_code')}
              </div>
            )}
            <LiveStreamButton url={wc(wedding,'live_streaming_url')} accentColor="#5A7A56" borderColor="rgba(90,122,86,.18)" />
          </Rev>
        </div>
      </section>

      {/* ── COUNTDOWN ── */}
      <section className="su-sec-alt">
        <div className="su-inner">
          <Rev>
            <div className="su-kicker">Ngitung Dinten</div>
            <div className="su-rule"><span>🌿</span></div>
            <div className="su-cd">
              {[["d","Dinten"],["h","Jam"],["m","Menit"],["s","Detik"]].map(([k,l]) => (
                <div key={k} className="su-cd-item">
                  <div className="su-cd-num">{String(countdown[k]).padStart(2,"0")}</div>
                  <div className="su-cd-unit">{l}</div>
                </div>
              ))}
            </div>
          </Rev>
        </div>
      </section>

      {/* ── LOVE STORY ── */}
      {wc(wedding,'love_story') && (
        <section className="su-sec">
          <div className="su-inner">
            <Rev>
              <div className="su-kicker">Carita Urang</div>
              <div className="su-h2">Perjalanan Cinta</div>
              <div className="su-rule"><span>🌸</span></div>
              <p className="su-love">{wc(wedding,'love_story')}</p>
            </Rev>
          </div>
        </section>
      )}

      {/* ── GALLERY ── */}
      {photos.length > 0 && (
        <section className="su-sec-alt">
          <div className="su-inner">
            <Rev>
              <div className="su-kicker">Galeri Foto</div>
              <div className="su-h2">Momen Berharga</div>
              <div className="su-rule"><span>🌿</span></div>
              <div className="su-gal">
                {photos.slice(0,1).map((p,i) => (
                  <div key={i} className="su-gal-wide">
                    <img src={p} alt="" onClick={() => setLb(i)} />
                  </div>
                ))}
                {photos.slice(1).map((p,i) => (
                  <div key={i+1} className="su-gal-sm">
                    <img src={p} alt="" onClick={() => setLb(i+1)} />
                  </div>
                ))}
              </div>
            </Rev>
          </div>
        </section>
      )}

      {/* ── RSVP ── */}
      <section className="su-sec">
        <div className="su-inner">
          <Rev>
            <div className="su-kicker">Konfirmasi Kehadiran</div>
            <div className="su-h2">RSVP</div>
            <div className="su-rule"><span>🌸</span></div>
            {rsvp.sent ? (
              <div style={{ fontStyle:"italic", color:"rgba(90,122,86,.68)" }}>✦ Hatur nuhun atas konfirmasinya.</div>
            ) : (
              <>
                <div className="su-rsvp-opts">
                  {[["hadir","✅ Hadir"],["mungkin","🤔 Mungkin"],["tidak_hadir","❌ Tidak Hadir"]].map(([v,l]) => (
                    <div key={v} className={`su-ropt${rsvp.rsvp===v?" sel":""}`} onClick={() => rsvp.setRsvp(v)}>{l}</div>
                  ))}
                </div>
                <textarea className="su-inp" rows={3} placeholder="Ucapan..."
                  value={rsvp.msg} onChange={e => rsvp.setMsg(e.target.value)} />
                <button className="su-btn" onClick={rsvp.submit} disabled={!rsvp.rsvp}>Kirim Konfirmasi</button>
              </>
            )}
          </Rev>
        </div>
      </section>

      {/* ── GUESTBOOK ── */}
      <section className="su-sec-alt">
        <div className="su-inner">
          <Rev>
            <div className="su-kicker">Buku Tamu</div>
            <div className="su-h2">Ucapan &amp; Doa</div>
            <div className="su-rule"><span>🌿</span></div>
            <LiveFeedTicker weddingId={wedding.id} accentColor="#5A7A56" textColor="rgba(20,40,18,0.65)" bgColor="rgba(90,122,86,0.04)" borderColor="rgba(90,122,86,0.1)" />
            {gb.sent ? (
              <div style={{ fontStyle:"italic", color:"rgba(90,122,86,.58)" }}>✦ Ucapanmu sudah terkirim.</div>
            ) : (
              <>
                <input className="su-inp" placeholder="Nama..."
                  value={gb.name} onChange={e => gb.setName(e.target.value)} />
                <textarea className="su-inp" rows={3} placeholder="Tulis ucapan..."
                  value={gb.msg} onChange={e => gb.setMsg(e.target.value)} />
                <button className="su-btn-ghost" onClick={gb.submit}>Kirim Ucapan</button>
              </>
            )}
          </Rev>
        </div>
      </section>

      {/* ── AMPLOP ── */}
      {banks.length > 0 && (
        <section className="su-sec">
          <div className="su-inner">
            <Rev>
              <div className="su-kicker">Amplop Digital</div>
              <div className="su-h2">Hadiah &amp; Doa</div>
              <div className="su-rule"><span>🌸</span></div>
              {banks.map((b,i) => (
                <div key={i} className="su-amp-row">
                  <div>
                    <div className="su-amp-bank">{b.bank||b.name}</div>
                    <div className="su-amp-num">{b.number||b.account}</div>
                    <div className="su-amp-holder">a.n. {b.holder}</div>
                  </div>
                  {copied===String(i)
                    ? <span style={{ fontFamily:"'Josefin Sans',sans-serif", fontSize:".4rem", color:"var(--ac)" }}>Disalin ✓</span>
                    : <button className="su-btn-ghost" style={{ padding:"5px 12px" }}
                        onClick={() => copyNum(b.number||b.account, String(i))}>Salin</button>
                  }
                </div>
              ))}
            </Rev>
          </div>
        </section>
      )}

      <div className="su-footer">
        <div style={{ fontFamily:"'Josefin Sans',sans-serif", fontSize:".42rem", letterSpacing:".42em", color:"rgba(255,255,255,.38)", fontWeight:200 }}>✦ MYKISAH ✦</div>
        <div style={{ fontFamily:"'EB Garamond',serif", fontSize:".78rem", fontStyle:"italic", color:"rgba(255,255,255,.22)", marginTop:3 }}>Hatur nuhun · Terima kasih</div>
      </div>

      {lb !== null && <LightboxModal photos={photos} startIndex={lb} onClose={() => setLb(null)} />}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// TEMA 9 — MINANGKABAU  (Rumah Gadang, gonjong SVG, merah-hitam-emas)
// ═══════════════════════════════════════════════════════════════
export function ThemeMinangkabau({ wedding, guest }) {
  const rsvp   = useRsvp(wedding, guest);
  const gb     = useGuestbook(wedding);
  const [lb, setLb]         = useState(null);
  const [copied, setCopied] = useState("");
  const countdown = useCountdown(wc(wedding,'target_date') || wedding.wedding_date);
  const photos = wcArr(wedding,'gallery').length ? wcArr(wedding,'gallery') : wcArr(wedding,'photos');
  const banks  = wcArr(wedding,'banks').length ? wcArr(wedding,'banks') : wcArr(wedding,'bank_accounts');

  function copyNum(num, key) {
    navigator.clipboard.writeText(num.replace(/\s/g,""));
    setCopied(key); setTimeout(() => setCopied(""), 2000);
  }

  /* Gonjong silhouette — atap runcing khas Minang */
  function Gonjong({ fill = "rgba(180,60,40,0.18)", h = 120 }) {
    return (
      <svg viewBox="0 0 800 120" preserveAspectRatio="none"
        style={{ width:"100%", height:h, display:"block" }}
        xmlns="http://www.w3.org/2000/svg">
        <path
          d="M0,120 L0,82 L80,22 L160,82 L200,58 L280,0 L360,58 L400,36 L440,58 L520,0 L600,58 L640,82 L720,22 L800,82 L800,120 Z"
          fill={fill}
        />
      </svg>
    );
  }

  const C = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;1,400&family=Cinzel:wght@400;500&display=swap');
    *{margin:0;padding:0;box-sizing:border-box;}
    :root{--bg:#0F0505;--bg2:#160A08;--red:#B43C28;--ac:#C9A84C;--tx:rgba(240,224,192,0.9);--txm:rgba(240,224,192,0.38);--bdr:rgba(180,60,40,0.14);}
    body{background:var(--bg);color:var(--tx);font-family:'Cormorant Garamond',serif;overflow-x:hidden;}
    @keyframes mgFadeUp{from{opacity:0;transform:translateY(26px)}to{opacity:1;transform:translateY(0)}}
    @keyframes mgSlideDown{from{opacity:0;transform:translateY(-18px)}to{opacity:1;transform:translateY(0)}}
    /* grid pattern */
    body::before{
      content:'';position:fixed;inset:0;pointer-events:none;z-index:0;
      background-image:
        repeating-linear-gradient(0deg,rgba(180,60,40,.025) 0px,rgba(180,60,40,.025) 1px,transparent 1px,transparent 24px),
        repeating-linear-gradient(90deg,rgba(201,168,76,.015) 0px,rgba(201,168,76,.015) 1px,transparent 1px,transparent 24px);
    }
    /* HERO */
    .mg-hero{min-height:100vh;display:flex;flex-direction:column;background:var(--bg);position:relative;z-index:1;overflow:hidden;}
    .mg-hero-top{background:linear-gradient(to bottom,#1A0A08,var(--bg));padding:52px 40px 36px;text-align:center;border-bottom:2px solid rgba(180,60,40,.28);}
    .mg-hero-body{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:36px 40px;text-align:center;position:relative;z-index:2;}
    .mg-hero-bot{position:relative;z-index:1;}
    .mg-bismillah{font-family:'Cormorant Garamond',serif;font-size:1.5rem;font-style:italic;color:rgba(201,168,76,.33);margin-bottom:14px;animation:mgSlideDown 1s ease both;}
    .mg-tag{font-family:'Cinzel',serif;font-size:12px;letter-spacing:.54em;color:rgba(180,60,40,.65);animation:mgFadeUp 1s ease .1s both;}
    .mg-guest{font-family:'Cinzel',serif;font-size:13px;letter-spacing:.22em;color:rgba(180,60,40,.55);margin-bottom:8px;animation:mgFadeUp 1s ease .15s both;}
    .mg-couple{font-family:'Cinzel',serif;font-size:clamp(2.3rem,9vw,5.2rem);line-height:.87;letter-spacing:.03em;animation:mgFadeUp 1.2s ease .22s both;}
    .mg-bride{color:var(--ac);}
    .mg-amp{display:block;font-family:'Cormorant Garamond',serif;font-size:.19em;font-style:italic;opacity:.17;margin:11px 0;letter-spacing:.22em;}
    .mg-border-box{border:1px solid rgba(180,60,40,.18);border-top:3px solid rgba(180,60,40,.38);padding:18px 22px;margin:22px auto;max-width:380px;animation:mgFadeUp 1s ease .34s both;}
    .mg-fullname{font-size:17px;color:rgba(240,224,192,.36);line-height:2.05;}
    .mg-date{font-family:'Cinzel',serif;font-size:13px;letter-spacing:.26em;color:var(--txm);margin-top:16px;animation:mgFadeUp 1s ease .42s both;}
    /* sections */
    .mg-sec{padding:80px 40px;position:relative;z-index:1;}
    .mg-sec-alt{padding:80px 40px;background:var(--bg2);position:relative;z-index:1;}
    .mg-inner{max-width:660px;margin:0 auto;}
    .mg-kicker{font-family:'Cinzel',serif;font-size:12px;letter-spacing:.52em;color:rgba(180,60,40,.58);text-align:center;margin-bottom:9px;}
    .mg-h2{font-family:'Cinzel',serif;font-size:clamp(1.55rem,5vw,2.3rem);text-align:center;letter-spacing:.04em;margin-bottom:14px;line-height:1.1;}
    .mg-orndiv{display:flex;align-items:center;gap:10px;justify-content:center;margin-bottom:24px;}
    .mg-orndiv::before,.mg-orndiv::after{content:'';flex:1;max-width:48px;height:1px;background:rgba(180,60,40,.14);}
    .mg-orndiv span{color:rgba(180,60,40,.38);font-size:16px;}
    /* events — top-border accent */
    .mg-ev{margin-bottom:9px;border:1px solid var(--bdr);border-top:3px solid rgba(180,60,40,.34);padding:20px;background:rgba(180,60,40,.03);}
    .mg-ev-type{font-family:'Cinzel',serif;font-size:12px;letter-spacing:.42em;color:var(--red);margin-bottom:9px;}
    .mg-ev-date{font-size:16px;font-style:italic;margin-bottom:3px;}
    .mg-ev-time{font-family:'Cinzel',serif;font-size:13px;letter-spacing:.16em;color:var(--txm);margin-bottom:8px;}
    .mg-ev-venue{font-size:17px;color:var(--txm);line-height:1.88;}
    /* countdown */
    .mg-cd{display:flex;border:1px solid var(--bdr);border-top:2px solid rgba(180,60,40,.28);}
    .mg-cd-item{flex:1;text-align:center;border-right:1px solid var(--bdr);padding:18px 6px;}
    .mg-cd-item:last-child{border-right:none;}
    .mg-cd-num{font-family:'Cinzel',serif;font-size:clamp(1.9rem,7vw,3rem);color:var(--ac);line-height:1;}
    .mg-cd-unit{font-family:'Cinzel',serif;font-size:12px;letter-spacing:.28em;color:var(--txm);margin-top:5px;}
    /* love */
    .mg-love{font-size:16px;line-height:2.25;color:var(--txm);font-style:italic;white-space:pre-line;text-align:center;}
    /* gallery */
    .mg-gal{display:grid;grid-template-columns:1fr 1fr;gap:3px;}
    .mg-gal-wide{grid-column:span 2;aspect-ratio:16/7;overflow:hidden;}
    .mg-gal-sm{aspect-ratio:4/3;overflow:hidden;}
    .mg-gal img{width:100%;height:100%;object-fit:cover;cursor:pointer;filter:sepia(.15) brightness(.78);transition:filter .4s;}
    .mg-gal img:hover{filter:sepia(.05) brightness(.9);}
    /* rsvp */
    .mg-rsvp-opts{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:12px;}
    .mg-ropt{padding:13px 7px;border:1px solid var(--bdr);border-top:2px solid rgba(180,60,40,.28);cursor:pointer;text-align:center;font-family:'Cinzel',serif;font-size:12px;letter-spacing:.13em;transition:all .25s;}
    .mg-ropt:hover,.mg-ropt.sel{border-color:var(--red);background:rgba(180,60,40,.05);color:var(--ac);}
    .mg-ricon{font-size:1rem;display:block;margin-bottom:5px;}
    .mg-inp{width:100%;padding:10px 12px;background:rgba(180,60,40,.03);border:1px solid var(--bdr);color:var(--tx);font-family:'Cormorant Garamond',serif;font-size:16px;font-style:italic;outline:none;resize:none;transition:border-color .25s;margin-bottom:9px;}
    .mg-inp:focus{border-color:var(--red);}
    .mg-inp::placeholder{color:var(--txm);}
    .mg-btn{padding:12px 26px;background:var(--red);color:var(--tx);font-family:'Cinzel',serif;font-size:13px;letter-spacing:.22em;border:none;cursor:pointer;transition:opacity .2s;}
    .mg-btn:hover{opacity:.85;}.mg-btn:disabled{opacity:.4;cursor:default;}
    .mg-btn-ghost{padding:10px 20px;background:transparent;border:1px solid rgba(180,60,40,.2);color:var(--ac);font-family:'Cinzel',serif;font-size:12px;letter-spacing:.18em;cursor:pointer;transition:all .25s;}
    .mg-btn-ghost:hover{background:rgba(180,60,40,.04);}
    /* amplop */
    .mg-amp-card{padding:16px;border:1px solid var(--bdr);border-top:2px solid rgba(180,60,40,.25);background:rgba(180,60,40,.03);display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;}
    .mg-amp-bank{font-family:'Cinzel',serif;font-size:12px;letter-spacing:.2em;color:var(--red);}
    .mg-amp-num{font-size:16px;margin:3px 0;font-style:italic;}
    .mg-amp-holder{font-size:16px;color:var(--txm);}
    /* footer */
    .mg-footer{padding:32px;text-align:center;border-top:3px solid rgba(180,60,40,.28);position:relative;z-index:1;}
    @media(max-width:540px){.mg-rsvp-opts{grid-template-columns:1fr;}.mg-gal{grid-template-columns:1fr;}.mg-gal-wide{grid-column:span 1;aspect-ratio:4/3;}}
  `;

  return (
    <>
      <style>{C}</style>

      {/* ── GONJONG + HERO ── */}
      <div className="mg-hero">
        {/* gonjong atas */}
        <div style={{ background:"#1A0A08", position:"relative", zIndex:1 }}>
          <Gonjong fill="rgba(180,60,40,.22)" h={100} />
        </div>

        <div className="mg-hero-top">
          <div className="mg-bismillah">بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيم</div>
          <div className="mg-tag">✦ UNDANGAN PERNIKAHAN MINANGKABAU ✦</div>
        </div>

        <div className="mg-hero-body">
          {guest && <div className="mg-guest">Kepada Yth. · {guest.name}</div>}
          <div className="mg-couple">
            {wedding.groom_name}
            <span className="mg-amp">&amp;</span>
            <span className="mg-bride">{wedding.bride_name}</span>
          </div>
          <div className="mg-border-box">
            <div className="mg-fullname">
              {wc(wedding,'groom_full') || wedding.groom_name}<br />
              <span style={{ opacity:.4, fontSize:".88rem" }}>&amp;</span><br />
              {wc(wedding,'bride_full') || wedding.bride_name}
            </div>
          </div>
          <div className="mg-date">{wc(wedding,'resepsi_date') || wc(wedding,'akad_date')}</div>
        </div>

        {/* gonjong bawah — dibalik */}
        <div className="mg-hero-bot" style={{ background:"#160A08", transform:"scaleY(-1)", marginTop:-1 }}>
          <Gonjong fill="rgba(180,60,40,.15)" h={80} />
        </div>
      </div>

      {/* ── KATA PEMBUKA ── */}
      {wc(wedding,'opening_text') && (
        <OpeningSection text={wc(wedding,'opening_text')} accentColor="#B43C28" textColor="rgba(240,230,220,.45)" borderColor="rgba(180,60,40,.12)" fontFamily="'Cormorant Garamond',serif" />
      )}
      <MusicPlayer url={wc(wedding,'music_url')} accentColor="#B43C28" dark={true} />

      {/* ── EVENTS ── */}
      <section className="mg-sec">
        <div className="mg-inner">
          <Rev>
            <div className="mg-kicker">Waktu &amp; Tempat</div>
            <div className="mg-h2">Hari Baralek</div>
            <div className="mg-orndiv"><span>✦</span></div>
            <div className="mg-ev">
              <div className="mg-ev-type">Akad Nikah</div>
              <div className="mg-ev-date">{wc(wedding,'akad_date')}</div>
              <div className="mg-ev-time">{wc(wedding,'akad_time')}</div>
              <div className="mg-ev-venue">{wc(wedding,'venue_name')} · {wc(wedding,'venue_address')}</div>
            </div>
            <div className="mg-ev">
              <div className="mg-ev-type">Resepsi / Baralek</div>
              <div className="mg-ev-date">{wc(wedding,'resepsi_date')}</div>
              <div className="mg-ev-time">{wc(wedding,'resepsi_time')}</div>
              <div className="mg-ev-venue">{wc(wedding,'venue_name')} · {wc(wedding,'venue_address')}</div>
            </div>
            {wc(wedding,'venue_maps_url') && (
              <div style={{ marginTop:13 }}>
                <a href={wc(wedding,'venue_maps_url')} target="_blank" rel="noopener"
                  style={{ fontFamily:"'Cinzel',serif", fontSize:".43rem",
                           letterSpacing:".2em", color:"var(--ac)", textDecoration:"none" }}>
                  📍 Peta Lokasi
                </a>
              </div>
            )}
            {wc(wedding,'dress_code') && (
              <div style={{ marginTop:14, fontFamily:"'Cinzel',serif", fontSize:".43rem",
                            letterSpacing:".18em", color:"var(--txm)" }}>
                Dress Code · {wc(wedding,'dress_code')}
              </div>
            )}
            <LiveStreamButton url={wc(wedding,'live_streaming_url')} accentColor="#B43C28" borderColor="rgba(180,60,40,.2)" />
          </Rev>
        </div>
      </section>

      {/* ── COUNTDOWN ── */}
      <section className="mg-sec-alt">
        <div className="mg-inner">
          <Rev>
            <div className="mg-kicker">Hitung Hari</div>
            <div className="mg-orndiv"><span>✦</span></div>
            <div className="mg-cd">
              {[["d","Hari"],["h","Jam"],["m","Menit"],["s","Detik"]].map(([k,l]) => (
                <div key={k} className="mg-cd-item">
                  <div className="mg-cd-num">{String(countdown[k]).padStart(2,"0")}</div>
                  <div className="mg-cd-unit">{l}</div>
                </div>
              ))}
            </div>
          </Rev>
        </div>
      </section>

      {/* ── LOVE STORY ── */}
      {wc(wedding,'love_story') && (
        <section className="mg-sec">
          <div className="mg-inner">
            <Rev>
              <div className="mg-kicker">Kisah Kami</div>
              <div className="mg-h2">Perjalanan Cinta</div>
              <div className="mg-orndiv"><span>✦</span></div>
              <p className="mg-love">{wc(wedding,'love_story')}</p>
            </Rev>
          </div>
        </section>
      )}

      {/* ── GALLERY ── */}
      {photos.length > 0 && (
        <section className="mg-sec-alt">
          <div className="mg-inner">
            <Rev>
              <div className="mg-kicker">Galeri Foto</div>
              <div className="mg-h2">Momen Berharga</div>
              <div className="mg-orndiv"><span>✦</span></div>
              <div className="mg-gal">
                {photos.slice(0,1).map((p,i) => (
                  <div key={i} className="mg-gal-wide">
                    <img src={p} alt="" onClick={() => setLb(i)} />
                  </div>
                ))}
                {photos.slice(1).map((p,i) => (
                  <div key={i+1} className="mg-gal-sm">
                    <img src={p} alt="" onClick={() => setLb(i+1)} />
                  </div>
                ))}
              </div>
            </Rev>
          </div>
        </section>
      )}

      {/* ── RSVP ── */}
      <section className="mg-sec">
        <div className="mg-inner" style={{ textAlign:"center" }}>
          <Rev>
            <div className="mg-kicker">Konfirmasi Kehadiran</div>
            <div className="mg-h2">RSVP</div>
            <div className="mg-orndiv"><span>✦</span></div>
            {rsvp.sent ? (
              <div style={{ fontStyle:"italic", color:"rgba(201,168,76,.65)" }}>✦ Terima kasih atas konfirmasimu.</div>
            ) : (
              <div style={{ textAlign:"left" }}>
                <div className="mg-rsvp-opts">
                  {[["hadir","✅","Hadir"],["mungkin","🤔","Mungkin"],["tidak_hadir","❌","Tidak Hadir"]].map(([v,ic,l]) => (
                    <div key={v} className={`mg-ropt${rsvp.rsvp===v?" sel":""}`} onClick={() => rsvp.setRsvp(v)}>
                      <span className="mg-ricon">{ic}</span>{l}
                    </div>
                  ))}
                </div>
                <textarea className="mg-inp" rows={3} placeholder="Ucapan..."
                  value={rsvp.msg} onChange={e => rsvp.setMsg(e.target.value)} />
                <div style={{ textAlign:"center" }}>
                  <button className="mg-btn" onClick={rsvp.submit} disabled={!rsvp.rsvp}>✦ Kirim Konfirmasi</button>
                </div>
              </div>
            )}
          </Rev>
        </div>
      </section>

      {/* ── GUESTBOOK ── */}
      <section className="mg-sec-alt">
        <div className="mg-inner" style={{ textAlign:"center" }}>
          <Rev>
            <div className="mg-kicker">Buku Tamu</div>
            <div className="mg-h2">Ucapan &amp; Doa</div>
            <div className="mg-orndiv"><span>✦</span></div>
            <LiveFeedTicker weddingId={wedding.id} accentColor="#C9A84C" textColor="rgba(240,232,216,0.65)" bgColor="rgba(201,168,76,0.04)" borderColor="rgba(180,60,40,0.15)" />
            {gb.sent ? (
              <div style={{ fontStyle:"italic", color:"rgba(201,168,76,.55)" }}>✦ Ucapanmu sudah terkirim.</div>
            ) : (
              <div style={{ textAlign:"left" }}>
                <input className="mg-inp" placeholder="Nama..."
                  value={gb.name} onChange={e => gb.setName(e.target.value)} />
                <textarea className="mg-inp" rows={3} placeholder="Tulis ucapan..."
                  value={gb.msg} onChange={e => gb.setMsg(e.target.value)} />
                <div style={{ textAlign:"center" }}>
                  <button className="mg-btn-ghost" onClick={gb.submit}>✦ Kirim Ucapan</button>
                </div>
              </div>
            )}
          </Rev>
        </div>
      </section>

      {/* ── AMPLOP ── */}
      {banks.length > 0 && (
        <section className="mg-sec">
          <div className="mg-inner">
            <Rev>
              <div className="mg-kicker">Amplop Digital</div>
              <div className="mg-h2">Hadiah &amp; Doa</div>
              <div className="mg-orndiv"><span>✦</span></div>
              {banks.map((b,i) => (
                <div key={i} className="mg-amp-card">
                  <div>
                    <div className="mg-amp-bank">{b.bank||b.name}</div>
                    <div className="mg-amp-num">{b.number||b.account}</div>
                    <div className="mg-amp-holder">a.n. {b.holder}</div>
                  </div>
                  {copied===String(i)
                    ? <span style={{ fontFamily:"'Cinzel',serif", fontSize:".43rem", color:"var(--ac)" }}>Disalin ✓</span>
                    : <button className="mg-btn-ghost" style={{ padding:"6px 14px" }}
                        onClick={() => copyNum(b.number||b.account, String(i))}>Salin</button>
                  }
                </div>
              ))}
            </Rev>
          </div>
        </section>
      )}

      {/* gonjong atas footer */}
      <div style={{ position:"relative", zIndex:1, background:"#0F0505" }}>
        <Gonjong fill="rgba(180,60,40,.12)" h={72} />
      </div>

      <div className="mg-footer">
        <div style={{ fontFamily:"'Cinzel',serif", fontSize:".49rem", letterSpacing:".42em", color:"rgba(201,168,76,.24)" }}>✦ MYKISAH ✦</div>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:".77rem", fontStyle:"italic", color:"rgba(240,224,192,.14)", marginTop:3 }}>
          Adat Basandi Syarak · Syarak Basandi Kitabullah
        </div>
      </div>

      {lb !== null && <LightboxModal photos={photos} startIndex={lb} onClose={() => setLb(null)} />}
    </>
  );
}

// ── THEME MAP — tambahkan tema baru di sini ──────────────────
const THEMES = {
  eternal:     ThemeEternal,
  serenity:    ThemeSerenity,
  amour:       ThemeAmour,
  twilight:    ThemeTwilight,
  ivory:       ThemeIvory,
  bloom:       ThemeBloom,
  priyayi:     ThemePriyayi,
  pasundan:    ThemePasundan,
  minangkabau: ThemeMinangkabau,
};

// ── MAIN EXPORT ──────────────────────────────────────────────
export default function Undangan() {
  const { slug }       = useParams();
  const [params]       = useSearchParams();
  const token          = params.get("token");
  const [wedding, setWedding]   = useState(null);
  const [guest,   setGuest]     = useState(null);
  const [status,  setStatus]    = useState("loading");
  const [opened,  setOpened]    = useState(false);

  useEffect(() => {
    async function load() {
      const w = await sbGet(`weddings?slug=eq.${slug}&status=eq.active&limit=1`);
      if (!w) return setStatus("not_found");
      if (w.expires_at && new Date(w.expires_at) < new Date()) return setStatus("expired");
      setWedding(w);
      if (token) {
        const g = await sbGet(`guests?wedding_id=eq.${w.id}&token=eq.${token}&limit=1`);
        if (!g) return setStatus("wrong_token");
        setGuest(g);
      }
      setStatus("ok");
    }
    load();
  }, [slug, token]);

  if (status === "loading") return <LoadingScreen />;
  if (status !== "ok")      return <ErrorScreen type={status} />;
  if (!opened)              return <EnvelopeScreen wedding={wedding} guest={guest} onOpen={() => setOpened(true)} />;

  const Theme = THEMES[wedding.theme] || ThemeEternal;
  return <Theme wedding={wedding} guest={guest} />;
}

// ── LOADING ──────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ minHeight:"100vh", background:"#0C0B09", display:"flex",
                  alignItems:"center", justifyContent:"center" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:28, height:28, border:"1px solid rgba(201,168,76,0.3)",
                      borderTopColor:"#C9A84C", borderRadius:"50%",
                      animation:"spin 1s linear infinite", margin:"0 auto 14px" }} />
        <div style={{ fontFamily:"sans-serif", fontSize:"0.46rem",
                      letterSpacing:"0.4em", color:"rgba(201,168,76,0.4)" }}>MEMUAT</div>
      </div>
    </div>
  );
}

// ── ERROR ────────────────────────────────────────────────────
function ErrorScreen({ type }) {
  const msgs = {
    not_found:   "Undangan tidak ditemukan.",
    expired:     "Masa aktif undangan telah habis.",
    wrong_token: "Link tamu tidak valid.",
  };
  return (
    <div style={{ minHeight:"100vh", background:"#0C0B09", display:"flex",
                  alignItems:"center", justifyContent:"center", padding:24,
                  fontFamily:"serif", color:"rgba(240,232,216,0.6)", textAlign:"center" }}>
      <div>
        <div style={{ fontSize:"1.6rem", marginBottom:10 }}>Oops</div>
        <div style={{ fontSize:"0.82rem" }}>{msgs[type]}</div>
      </div>
    </div>
  );
}

// ── ENVELOPE ─────────────────────────────────────────────────
const THEME_META = {
  eternal:     { bg:"#0C0B09", ac:"#C9A84C", dark:true },
  serenity:    { bg:"#060D08", ac:"#7A9E7E", dark:true },
  amour:       { bg:"#120608", ac:"#C97B6F", dark:true },
  twilight:    { bg:"#08070F", ac:"#9B87B8", dark:true },
  ivory:       { bg:"#F5F0E8", ac:"#8B6F47", dark:false },
  bloom:       { bg:"#FAFAF7", ac:"#7A9E7E", dark:false },
  priyayi:     { bg:"#1A1105", ac:"#C9A84C", dark:true },
  pasundan:    { bg:"#F7F5EE", ac:"#5A7A56", dark:false },
  minangkabau: { bg:"#0F0505", ac:"#C9A84C", dark:true },
};

function EnvelopeScreen({ wedding, guest, onOpen }) {
  const meta  = THEME_META[wedding.theme] || THEME_META.eternal;
  const { bg, ac, dark } = meta;
  const tx  = dark ? "rgba(240,232,216,0.88)" : "rgba(28,28,22,0.88)";
  const txm = dark ? "rgba(240,232,216,0.3)"  : "rgba(28,28,22,0.3)";
  return (
    <div style={{ minHeight:"100vh", background:bg, display:"flex",
                  alignItems:"center", justifyContent:"center", padding:40,
                  fontFamily:"serif", overflow:"hidden", position:"relative" }}>
      <style>{`
        @keyframes envFadeUp{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
        .env-wrap{animation:envFadeUp 1.2s ease both;}
        .env-btn{cursor:pointer;transition:all 0.3s;}
        .env-btn:hover{opacity:0.7;}
      `}</style>
      <div style={{ position:"absolute", inset:0,
                    background:`radial-gradient(ellipse 55% 45% at 50% 50%, ${ac}0a, transparent 68%)`,
                    pointerEvents:"none" }} />
      <div className="env-wrap" style={{ textAlign:"center", maxWidth:400, position:"relative", zIndex:2 }}>
        <div style={{ fontSize:"0.44rem", letterSpacing:"0.58em", color:ac,
                      opacity:0.6, marginBottom:32, fontFamily:"sans-serif" }}>
          ✦ UNDANGAN PERNIKAHAN ✦
        </div>
        {guest && (
          <>
            <div style={{ fontSize:"0.6rem", color:txm, letterSpacing:"0.1em", marginBottom:6 }}>Kepada Yth.</div>
            <div style={{ fontSize:"1.1rem", fontStyle:"italic", color:`${ac}cc`, marginBottom:26 }}>{guest.name}</div>
          </>
        )}
        <div style={{ fontSize:"clamp(2.8rem,11vw,5rem)", lineHeight:0.88, color:tx, letterSpacing:"0.02em" }}>
          {wedding.groom_name}
          <span style={{ display:"block", fontSize:"0.2em", fontStyle:"italic",
                         opacity:0.18, margin:"12px 0", letterSpacing:"0.22em" }}>&amp;</span>
          <span style={{ color:ac }}>{wedding.bride_name}</span>
        </div>
        <div style={{ width:44, height:1, background:`${ac}33`, margin:"26px auto" }} />
        <div style={{ fontSize:"0.5rem", letterSpacing:"0.28em", color:txm, fontFamily:"sans-serif" }}>
          {wc(wedding,'resepsi_date') || wc(wedding,'akad_date')}
        </div>
        <button className="env-btn" onClick={onOpen}
          style={{ marginTop:38, padding:"12px 34px", background:"transparent",
                   border:`1px solid ${ac}44`, color:ac,
                   fontFamily:"sans-serif", fontSize:"0.5rem", letterSpacing:"0.26em" }}>
          ✦ Buka Undangan
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TEMA 1 — ETERNAL  (cinematic, gold particles, full dark scroll)
// ═══════════════════════════════════════════════════════════════
export function ThemeEternal({ wedding, guest }) {
  const canvasRef = useRef();
  const rsvp   = useRsvp(wedding, guest);
  const gb     = useGuestbook(wedding);
  const [lb, setLb]       = useState(null);
  const [copied, setCopied] = useState("");
  const countdown = useCountdown(wc(wedding,'target_date') || wedding.wedding_date);
  const photos = wcArr(wedding,'gallery').length ? wcArr(wedding,'gallery') : wcArr(wedding,'photos');
  const banks  = wcArr(wedding,'banks').length ? wcArr(wedding,'banks') : wcArr(wedding,'bank_accounts');

  /* gold particle canvas */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    const pts = Array.from({ length: 72 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.4 + 0.3,
      vy: -(Math.random() * 0.35 + 0.08),
      vx: (Math.random() - 0.5) * 0.12,
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pts.forEach(p => {
        p.y += p.vy; p.x += p.vx;
        if (p.y < -6) p.y = canvas.height + 6;
        const o = Math.abs(Math.sin(Date.now() / 3200 + p.x * 0.01)) * 0.38;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${o})`; ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);

  function copyNum(num, key) {
    navigator.clipboard.writeText(num.replace(/\s/g,""));
    setCopied(key); setTimeout(() => setCopied(""), 2000);
  }

  const C = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;1,400&family=Cinzel:wght@400;500&display=swap');
    *{margin:0;padding:0;box-sizing:border-box;}
    :root{--bg:#0C0B09;--bg2:#0E0B07;--ac:#C9A84C;--tx:rgba(240,232,216,0.9);--txm:rgba(240,232,216,0.38);--bdr:rgba(201,168,76,0.12);}
    html{scroll-behavior:smooth; font-size:16px; }
    body{background:var(--bg);color:var(--tx);font-family:'Cormorant Garamond',serif;overflow-x:hidden;}
    @keyframes etFadeUp{from{opacity:0;transform:translateY(38px)}to{opacity:1;transform:translateY(0)}}
    @keyframes etFadeIn{from{opacity:0}to{opacity:1}}
    @keyframes etShimmer{0%,100%{opacity:0.35}50%{opacity:1}}
    /* hero */
    .et-hero{min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:80px 28px;position:relative;z-index:1;}
    .et-hero-radial{position:absolute;inset:0;background:radial-gradient(ellipse 70% 55% at 50% 38%,rgba(201,168,76,0.055),transparent 68%);pointer-events:none;}
    .et-tag{font-family:'Cinzel',serif;font-size:13px;letter-spacing:.55em;color:rgba(201,168,76,.55);margin-bottom:32px;animation:etFadeIn 1.6s ease both;}
    .et-guest-to{font-size:15px;color:var(--txm);letter-spacing:.1em;margin-bottom:6px;animation:etFadeUp 1s ease .15s both;}
    .et-guest-name{font-family:'Cormorant Garamond',serif;font-size:1.15rem;font-style:italic;color:rgba(201,168,76,.75);margin-bottom:24px;animation:etFadeUp 1s ease .22s both;}
    .et-couple{font-family:'Cinzel',serif;font-size:clamp(3.4rem,13vw,6.8rem);line-height:.86;letter-spacing:.04em;animation:etFadeUp 1.3s ease .28s both;}
    .et-bride{color:var(--ac);}
    .et-amp{display:block;font-family:'Cormorant Garamond',serif;font-size:.17em;font-style:italic;opacity:.18;margin:13px 0;letter-spacing:.22em;}
    .et-rule{width:44px;height:1px;background:rgba(201,168,76,.18);margin:28px auto;animation:etFadeIn 1.4s ease .7s both;}
    .et-date{font-family:'Cinzel',serif;font-size:14px;letter-spacing:.36em;color:var(--txm);animation:etFadeUp 1s ease .6s both;}
    .et-scroll{position:absolute;bottom:30px;display:flex;flex-direction:column;align-items:center;gap:8px;animation:etFadeIn 2s ease 1.2s both;}
    .et-scroll-label{font-family:'Cinzel',serif;font-size:12px;letter-spacing:.3em;color:rgba(201,168,76,.3);}
    .et-scroll-line{width:1px;height:46px;background:linear-gradient(to bottom,var(--ac),transparent);animation:etShimmer 2.2s ease infinite;}
    /* divider */
    .et-cdivider{height:1px;background:linear-gradient(to right,transparent,rgba(201,168,76,.18),transparent);}
    /* section */
    .et-sec{padding:96px 28px;max-width:700px;margin:0 auto;position:relative;z-index:1;}
    .et-sec-dark{background:var(--bg2);position:relative;z-index:1;}
    .et-label{font-family:'Cinzel',serif;font-size:13px;letter-spacing:.5em;color:rgba(201,168,76,.6);text-align:center;margin-bottom:10px;}
    .et-h2{font-family:'Cinzel',serif;font-size:clamp(1.7rem,5.5vw,2.7rem);text-align:center;letter-spacing:.05em;margin-bottom:26px;line-height:1.1;}
    .et-rule2{width:44px;height:1px;background:var(--bdr);margin:0 auto 32px;}
    /* events */
    .et-ev-grid{display:grid;grid-template-columns:1fr 1px 1fr;margin-bottom:28px;}
    .et-ev-sep{background:var(--bdr);}
    .et-ev-card{padding:28px 22px;text-align:center;}
    .et-ev-type{font-family:'Cinzel',serif;font-size:12px;letter-spacing:.42em;color:var(--ac);margin-bottom:13px;}
    .et-ev-date{font-size:1rem;font-style:italic;margin-bottom:4px;}
    .et-ev-time{font-family:'Cinzel',serif;font-size:13px;letter-spacing:.17em;color:var(--txm);margin-bottom:11px;}
    .et-ev-venue{font-size:17px;color:var(--txm);line-height:1.95;}
    .et-ev-venue strong{display:block;color:var(--tx);font-weight:300;margin-bottom:3px;font-size:15px;}
    .et-maps{display:inline-flex;align-items:center;gap:7px;margin-top:14px;padding:9px 18px;border:1px solid var(--bdr);color:var(--ac);font-family:'Cinzel',serif;font-size:13px;letter-spacing:.2em;text-decoration:none;transition:all .3s;}
    .et-maps:hover{background:rgba(201,168,76,.05);}
    /* countdown */
    .et-cd{display:flex;}
    .et-cd-item{flex:1;text-align:center;border-right:1px solid rgba(255,255,255,.05);padding:18px 8px;}
    .et-cd-item:last-child{border-right:none;}
    .et-cd-num{font-family:'Cinzel',serif;font-size:clamp(2rem,7.5vw,3.2rem);color:var(--ac);line-height:1;}
    .et-cd-unit{font-family:'Cinzel',serif;font-size:12px;letter-spacing:.28em;color:var(--txm);margin-top:6px;}
    /* gallery */
    .et-gal{display:grid;grid-template-columns:1fr 1fr;grid-template-rows:auto auto;gap:3px;}
    .et-gal-main{grid-column:1/3;aspect-ratio:16/8;overflow:hidden;}
    .et-gal-sm{aspect-ratio:4/3;overflow:hidden;}
    .et-gal-img{width:100%;height:100%;object-fit:cover;cursor:pointer;transition:transform .6s ease,filter .4s;filter:brightness(.8);}
    .et-gal-img:hover{transform:scale(1.04);filter:brightness(1);}
    /* love story */
    .et-love{font-size:16px;line-height:2.3;color:var(--txm);font-style:italic;text-align:center;white-space:pre-line;}
    /* rsvp */
    .et-rsvp-opts{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:14px;}
    .et-ropt{padding:15px 8px;border:1px solid var(--bdr);cursor:pointer;text-align:center;transition:all .25s;font-family:'Cinzel',serif;font-size:13px;letter-spacing:.13em;}
    .et-ropt:hover,.et-ropt.sel{border-color:var(--ac);background:rgba(201,168,76,.05);color:var(--ac);}
    .et-ricon{font-size:1.1rem;display:block;margin-bottom:6px;}
    /* inputs */
    .et-inp{width:100%;padding:11px 13px;background:rgba(255,255,255,.02);border:1px solid var(--bdr);color:var(--tx);font-family:'Cormorant Garamond',serif;font-size:16px;font-style:italic;outline:none;resize:none;transition:border-color .25s;margin-bottom:10px;}
    .et-inp:focus{border-color:var(--ac);}
    .et-inp::placeholder{color:var(--txm);}
    /* buttons */
    .et-btn{width:100%;padding:13px;background:var(--ac);color:var(--bg);font-family:'Cinzel',serif;font-size:14px;letter-spacing:.26em;border:none;cursor:pointer;transition:opacity .2s;}
    .et-btn:hover{opacity:.84;}
    .et-btn:disabled{opacity:.45;cursor:default;}
    .et-btn-ghost{padding:10px 22px;background:transparent;border:1px solid var(--bdr);color:var(--ac);font-family:'Cinzel',serif;font-size:13px;letter-spacing:.2em;cursor:pointer;transition:all .25s;}
    .et-btn-ghost:hover{background:rgba(201,168,76,.05);border-color:var(--ac);}
    /* amplop */
    .et-amp-card{padding:18px 20px;background:rgba(255,255,255,.02);border:1px solid var(--bdr);display:flex;justify-content:space-between;align-items:center;gap:12px;margin-bottom:8px;}
    .et-amp-bank{font-family:'Cinzel',serif;font-size:13px;letter-spacing:.22em;color:var(--ac);}
    .et-amp-num{font-family:'Cinzel',serif;font-size:16px;letter-spacing:.07em;margin:5px 0;}
    .et-amp-holder{font-size:15px;color:var(--txm);}
    /* guestbook comments */
    .et-gb-item{padding:16px 18px;background:rgba(255,255,255,.02);border:1px solid var(--bdr);margin-bottom:8px;}
    .et-gb-name{font-family:'Cinzel',serif;font-size:13px;letter-spacing:.16em;color:var(--ac);margin-bottom:6px;}
    .et-gb-text{font-size:15px;color:var(--txm);font-style:italic;line-height:1.9;}
    /* footer */
    .et-footer{padding:38px 28px;text-align:center;background:var(--bg2);border-top:1px solid var(--bdr);position:relative;z-index:1;}
    /* dress code badge */
    .et-dc{text-align:center;margin-top:20px;font-family:'Cinzel',serif;font-size:13px;letter-spacing:.22em;color:var(--txm);}
    @media(max-width:580px){
      .et-ev-grid{grid-template-columns:1fr;grid-template-rows:auto;}.et-ev-sep{width:100%;height:1px;}.et-rsvp-opts{grid-template-columns:1fr;}
    }
  `;

  return (
    <>
      <style>{C}</style>
      <canvas ref={canvasRef} style={{ position:"fixed", inset:0, pointerEvents:"none", zIndex:0 }} />

      {/* ── HERO ── */}
      <section className="et-hero">
        <div className="et-hero-radial" />
        <div className="et-tag">✦ UNDANGAN PERNIKAHAN ✦</div>
        {guest && (
          <>
            <div className="et-guest-to">Kepada Yth.</div>
            <div className="et-guest-name">{guest.name}</div>
          </>
        )}
        <div className="et-couple">
          {wedding.groom_name}
          <span className="et-amp">&amp;</span>
          <span className="et-bride">{wedding.bride_name}</span>
        </div>
        <div className="et-rule" />
        <div className="et-date">{wc(wedding,'resepsi_date') || wc(wedding,'akad_date')}</div>
        <div className="et-scroll">
          <div className="et-scroll-label">Gulir</div>
          <div className="et-scroll-line" />
        </div>
      </section>

      <div className="et-cdivider" style={{ position:"relative", zIndex:1 }} />

      {/* ── KATA PEMBUKA ── */}
      {wc(wedding,'opening_text') && (
        <div style={{ position:"relative", zIndex:1, background:"var(--bg2)" }}>
          <OpeningSection
            text={wc(wedding,'opening_text')}
            accentColor="#C9A84C"
            textColor="rgba(240,232,216,.48)"
            borderColor="rgba(201,168,76,.1)"
            fontFamily="'Cormorant Garamond',serif"
          />
        </div>
      )}

      {/* ── MUSIC PLAYER ── */}
      <MusicPlayer url={wc(wedding,'music_url')} accentColor="#C9A84C" dark={true} />

      {/* ── COUNTDOWN ── */}
      <div className="et-sec-dark" style={{ padding:"72px 0" }}>
        <Rev>
          <div style={{ maxWidth:460, margin:"0 auto", padding:"0 28px" }}>
            <div className="et-label">Menghitung Hari</div>
            <div style={{ marginTop:20 }}>
              <div className="et-cd">
                {[["d","Hari"],["h","Jam"],["m","Menit"],["s","Detik"]].map(([k,l]) => (
                  <div key={k} className="et-cd-item">
                    <div className="et-cd-num">{String(countdown[k]).padStart(2,"0")}</div>
                    <div className="et-cd-unit">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Rev>
      </div>

      <div className="et-cdivider" style={{ position:"relative", zIndex:1 }} />

      {/* ── EVENTS ── */}
      <div style={{ position:"relative", zIndex:1 }}>
        <div className="et-sec">
          <Rev>
            <div className="et-label">Waktu &amp; Tempat</div>
            <div className="et-h2">{wedding.groom_name} &amp; {wedding.bride_name}</div>
            <div className="et-rule2" />
            <div className="et-ev-grid">
              <div className="et-ev-card">
                <div className="et-ev-type">Akad Nikah</div>
                <div className="et-ev-date">{wc(wedding,'akad_date')}</div>
                <div className="et-ev-time">{wc(wedding,'akad_time')}</div>
                <div className="et-ev-venue"><strong>{wc(wedding,'venue_name')}</strong>{wc(wedding,'venue_address')}</div>
              </div>
              <div className="et-ev-sep" />
              <div className="et-ev-card">
                <div className="et-ev-type">Resepsi</div>
                <div className="et-ev-date">{wc(wedding,'resepsi_date')}</div>
                <div className="et-ev-time">{wc(wedding,'resepsi_time')}</div>
                <div className="et-ev-venue"><strong>{wc(wedding,'venue_name')}</strong>{wc(wedding,'venue_address')}</div>
              </div>
            </div>
            {wc(wedding,'venue_maps_url') && (
              <div style={{ textAlign:"center" }}>
                <a href={wc(wedding,'venue_maps_url')} target="_blank" rel="noopener" className="et-maps">📍 Buka Peta</a>
              </div>
            )}
            {wc(wedding,'dress_code') && <div className="et-dc">Dress Code · {wc(wedding,'dress_code')}</div>}
            <LiveStreamButton url={wc(wedding,'live_streaming_url')} accentColor="#C9A84C" borderColor="rgba(201,168,76,.2)" />
          </Rev>
        </div>
      </div>

      {/* ── LOVE STORY ── */}
      {wc(wedding,'love_story') && (
        <div className="et-sec-dark" style={{ position:"relative", zIndex:1 }}>
          <div className="et-sec">
            <Rev>
              <div className="et-label">Kisah Kami</div>
              <div className="et-h2">Perjalanan Cinta</div>
              <div className="et-rule2" />
              <p className="et-love">{wc(wedding,'love_story')}</p>
            </Rev>
          </div>
        </div>
      )}

      {/* ── GALLERY ── */}
      {photos.length > 0 && (
        <div style={{ position:"relative", zIndex:1 }}>
          <div className="et-sec">
            <Rev>
              <div className="et-label">Galeri Foto</div>
              <div className="et-h2">Momen Berharga</div>
              <div className="et-rule2" />
              <div className="et-gal">
                {photos.slice(0,1).map((p,i) => (
                  <div key={i} className="et-gal-main">
                    <img src={p} alt="" className="et-gal-img" onClick={() => setLb(i)} />
                  </div>
                ))}
                {photos.slice(1,5).map((p,i) => (
                  <div key={i+1} className="et-gal-sm">
                    <img src={p} alt="" className="et-gal-img" onClick={() => setLb(i+1)} />
                  </div>
                ))}
              </div>
            </Rev>
          </div>
        </div>
      )}

      {/* ── RSVP ── */}
      <div className="et-sec-dark" style={{ position:"relative", zIndex:1 }}>
        <div className="et-sec">
          <Rev>
            <div className="et-label">Konfirmasi Kehadiran</div>
            <div className="et-h2">RSVP</div>
            <div className="et-rule2" />
            {rsvp.sent ? (
              <div style={{ textAlign:"center", padding:"36px 0", fontStyle:"italic", color:"rgba(201,168,76,.7)", fontSize:"1.05rem" }}>
                ✦ Terima kasih atas konfirmasimu.
              </div>
            ) : (
              <>
                <div className="et-rsvp-opts">
                  {[["hadir","✅","Hadir"],["mungkin","🤔","Mungkin"],["tidak_hadir","❌","Tidak Hadir"]].map(([v,ic,l]) => (
                    <div key={v} className={`et-ropt${rsvp.rsvp===v?" sel":""}`} onClick={() => rsvp.setRsvp(v)}>
                      <span className="et-ricon">{ic}</span>{l}
                    </div>
                  ))}
                </div>
                <textarea className="et-inp" rows={3}
                  placeholder="Ucapan untuk kedua mempelai..."
                  value={rsvp.msg} onChange={e => rsvp.setMsg(e.target.value)} />
                <button className="et-btn" onClick={rsvp.submit}
                  disabled={!rsvp.rsvp || rsvp.sending}>
                  {rsvp.sending ? "Mengirim..." : "✦ Kirim Konfirmasi"}
                </button>
              </>
            )}
          </Rev>
        </div>
      </div>

      {/* ── GUESTBOOK ── */}
      <div style={{ position:"relative", zIndex:1 }}>
        <div className="et-sec">
          <Rev>
            <div className="et-label">Buku Tamu</div>
            <div className="et-h2">Ucapan &amp; Doa</div>
            <div className="et-rule2" />
            <LiveFeedTicker weddingId={wedding.id} accentColor="#C9A84C" textColor="rgba(240,232,216,0.65)" bgColor="rgba(201,168,76,0.04)" borderColor="rgba(201,168,76,0.1)" />
            {gb.sent ? (
              <div style={{ textAlign:"center", color:"rgba(201,168,76,.6)", fontStyle:"italic" }}>✦ Ucapanmu sudah terkirim.</div>
            ) : (
              <>
                <input className="et-inp" placeholder="Namamu..."
                  value={gb.name} onChange={e => gb.setName(e.target.value)} />
                <textarea className="et-inp" rows={3} placeholder="Tulis ucapan..."
                  value={gb.msg} onChange={e => gb.setMsg(e.target.value)} />
                <button className="et-btn-ghost" onClick={gb.submit}>✦ Kirim Ucapan</button>
              </>
            )}
          </Rev>
        </div>
      </div>

      {/* ── AMPLOP DIGITAL ── */}
      {banks.length > 0 && (
        <div className="et-sec-dark" style={{ position:"relative", zIndex:1 }}>
          <div className="et-sec">
            <Rev>
              <div className="et-label">Amplop Digital</div>
              <div className="et-h2">Hadiah &amp; Doa</div>
              <div className="et-rule2" />
              {banks.map((b,i) => (
                <div key={i} className="et-amp-card">
                  <div>
                    <div className="et-amp-bank">{b.bank || b.name}</div>
                    <div className="et-amp-num">{b.number || b.account}</div>
                    <div className="et-amp-holder">a.n. {b.holder}</div>
                  </div>
                  {copied === String(i)
                    ? <span style={{ fontFamily:"'Cinzel',serif", fontSize:".46rem", color:"var(--ac)", letterSpacing:".14em" }}>Disalin ✓</span>
                    : <button className="et-btn-ghost" style={{ padding:"7px 14px", fontSize:".44rem" }}
                        onClick={() => copyNum(b.number||b.account, String(i))}>Salin</button>
                  }
                </div>
              ))}
            </Rev>
          </div>
        </div>
      )}

      <div className="et-footer">
        <div style={{ fontFamily:"'Cinzel',serif", fontSize:".52rem", letterSpacing:".42em", color:"rgba(201,168,76,.28)", marginBottom:4 }}>✦ MYKISAH ✦</div>
        <div style={{ fontSize:".56rem", color:"rgba(240,232,216,.12)" }}>Dibuat dengan cinta</div>
      </div>

      {lb !== null && <LightboxModal photos={photos} startIndex={lb} onClose={() => setLb(null)} />}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// TEMA 2 — SERENITY  (editorial majalah, dua kolom)
// ═══════════════════════════════════════════════════════════════
export function ThemeSerenity({ wedding, guest }) {
  const rsvp   = useRsvp(wedding, guest);
  const gb     = useGuestbook(wedding);
  const [lb, setLb]         = useState(null);
  const [copied, setCopied] = useState("");
  const countdown = useCountdown(wc(wedding,'target_date') || wedding.wedding_date);
  const photos = wcArr(wedding,'gallery').length ? wcArr(wedding,'gallery') : wcArr(wedding,'photos');
  const banks  = wcArr(wedding,'banks').length ? wcArr(wedding,'banks') : wcArr(wedding,'bank_accounts');
  const coverPhoto = photos[0] || "https://images.unsplash.com/photo-1519741497674-611481863552?w=900&q=80";

  function copyNum(num, key) {
    navigator.clipboard.writeText(num.replace(/\s/g,""));
    setCopied(key); setTimeout(() => setCopied(""), 2000);
  }

  const C = `
    @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;1,400&family=Josefin+Sans:wght@200;300;400&display=swap');
    *{margin:0;padding:0;box-sizing:border-box;}
    :root{--bg:#060D08;--bg2:#0A110B;--ac:#7A9E7E;--tx:rgba(216,235,216,0.88);--txm:rgba(216,235,216,0.38);--bdr:rgba(122,158,126,0.12);}
    body{background:var(--bg);color:var(--tx);font-family:'EB Garamond',serif;overflow-x:hidden;}
    @keyframes srSlideLeft{from{opacity:0;transform:translateX(-32px)}to{opacity:1;transform:translateX(0)}}
    @keyframes srSlideRight{from{opacity:0;transform:translateX(32px)}to{opacity:1;transform:translateX(0)}}
    @keyframes srFadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
    /* hero — magazine spread */
    .sr-hero{min-height:100vh;display:grid;grid-template-columns:1fr 1fr;}
    .sr-left{background:var(--bg);display:flex;flex-direction:column;justify-content:center;padding:80px 48px 80px 44px;border-right:1px solid var(--bdr);}
    .sr-right{position:relative;overflow:hidden;}
    .sr-cover{width:100%;height:100%;object-fit:cover;filter:brightness(.48) saturate(.6);}
    .sr-cover-overlay{position:absolute;inset:0;background:linear-gradient(to right,var(--bg),transparent 45%);}
    .sr-issue{font-family:'Josefin Sans',sans-serif;font-size:13px;letter-spacing:.56em;color:rgba(122,158,126,.5);margin-bottom:38px;animation:srSlideLeft 1s ease both;}
    .sr-vol{font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.4em;color:rgba(122,158,126,.28);margin-bottom:8px;animation:srSlideLeft 1s ease .05s both;}
    .sr-names{font-family:'EB Garamond',serif;font-size:clamp(2.8rem,6.5vw,5.5rem);line-height:.88;animation:srSlideLeft 1.1s ease .12s both;}
    .sr-bride-name{color:var(--ac);font-style:italic;}
    .sr-amp{display:block;font-family:'Josefin Sans',sans-serif;font-size:.19em;letter-spacing:.5em;opacity:.17;margin:10px 0;font-weight:200;}
    .sr-guest-label{font-family:'Josefin Sans',sans-serif;font-size:13px;letter-spacing:.22em;color:rgba(122,158,126,.5);margin-top:22px;animation:srSlideLeft 1s ease .3s both;}
    .sr-date{font-family:'Josefin Sans',sans-serif;font-size:14px;letter-spacing:.28em;color:var(--txm);margin-top:8px;animation:srSlideLeft 1s ease .34s both;}
    .sr-caption{position:absolute;bottom:24px;right:20px;font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.22em;color:rgba(216,235,216,.22);writing-mode:vertical-rl;}
    /* content */
    .sr-body{max-width:920px;margin:0 auto;padding:0 36px;}
    .sr-spread{display:grid;grid-template-columns:1fr 1fr;gap:52px;padding:80px 0;border-bottom:1px solid var(--bdr);}
    .sr-single{padding:80px 0;border-bottom:1px solid var(--bdr);}
    .sr-kicker{font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.56em;color:rgba(122,158,126,.65);margin-bottom:11px;}
    .sr-h2{font-family:'EB Garamond',serif;font-size:clamp(1.7rem,3.8vw,2.6rem);line-height:1.1;margin-bottom:18px;}
    .sr-rule{width:32px;height:1px;background:rgba(122,158,126,.22);margin-bottom:20px;}
    .sr-pullquote{border-left:2px solid var(--ac);padding-left:18px;margin:22px 0;font-size:1.1rem;font-style:italic;color:rgba(216,235,216,.55);line-height:1.85;}
    .sr-body-text{font-size:16px;line-height:2.15;color:var(--txm);font-style:italic;}
    /* event card */
    .sr-ev{padding:22px;border:1px solid var(--bdr);margin-bottom:8px;}
    .sr-ev-type{font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.42em;color:var(--ac);margin-bottom:9px;}
    .sr-ev-date{font-size:1rem;font-style:italic;margin-bottom:3px;}
    .sr-ev-time{font-family:'Josefin Sans',sans-serif;font-size:13px;letter-spacing:.16em;color:var(--txm);margin-bottom:9px;}
    .sr-ev-venue{font-size:17px;color:var(--txm);line-height:1.85;}
    /* countdown */
    .sr-cd{display:flex;gap:0;margin-top:14px;}
    .sr-cd-item{flex:1;text-align:center;border-right:1px solid var(--bdr);padding:14px 6px;}
    .sr-cd-item:last-child{border-right:none;}
    .sr-cd-num{font-family:'Josefin Sans',sans-serif;font-size:clamp(1.7rem,6vw,2.8rem);color:var(--ac);line-height:1;font-weight:200;}
    .sr-cd-unit{font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.26em;color:var(--txm);margin-top:5px;font-weight:200;}
    /* photo grid */
    .sr-photo-tall{width:100%;aspect-ratio:3/4;object-fit:cover;filter:brightness(.72) saturate(.65);cursor:pointer;transition:filter .4s;}
    .sr-photo-tall:hover{filter:brightness(.88) saturate(.8);}
    .sr-photo-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:3px;}
    .sr-photo-sq{width:100%;aspect-ratio:1/1;object-fit:cover;filter:brightness(.72) saturate(.65);cursor:pointer;transition:filter .4s;}
    .sr-photo-sq:hover{filter:brightness(.88) saturate(.8);}
    /* rsvp */
    .sr-rsvp-opts{display:flex;gap:6px;margin-bottom:14px;}
    .sr-ropt{flex:1;padding:12px 6px;border:1px solid var(--bdr);cursor:pointer;text-align:center;font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.14em;color:var(--txm);transition:all .25s;}
    .sr-ropt:hover,.sr-ropt.sel{border-color:var(--ac);color:var(--ac);background:rgba(122,158,126,.05);}
    .sr-inp{width:100%;padding:10px 0;background:transparent;border:none;border-bottom:1px solid var(--bdr);color:var(--tx);font-family:'EB Garamond',serif;font-size:16px;font-style:italic;outline:none;resize:none;transition:border-color .25s;margin-bottom:12px;}
    .sr-inp:focus{border-bottom-color:var(--ac);}
    .sr-inp::placeholder{color:var(--txm);}
    .sr-btn{padding:11px 28px;background:var(--ac);color:var(--bg);font-family:'Josefin Sans',sans-serif;font-size:13px;letter-spacing:.24em;border:none;cursor:pointer;transition:opacity .2s;}
    .sr-btn:hover{opacity:.85;}
    .sr-btn:disabled{opacity:.4;cursor:default;}
    .sr-btn-ghost{padding:10px 22px;background:transparent;border:1px solid var(--bdr);color:var(--ac);font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.2em;cursor:pointer;transition:all .25s;}
    .sr-btn-ghost:hover{background:rgba(122,158,126,.05);}
    /* amplop */
    .sr-amp-row{padding:15px 0;border-bottom:1px solid var(--bdr);display:flex;justify-content:space-between;align-items:center;}
    .sr-amp-bank{font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.22em;color:var(--ac);}
    .sr-amp-num{font-size:16px;margin:4px 0;font-style:italic;}
    .sr-amp-holder{font-size:15px;color:var(--txm);}
    /* footer */
    .sr-footer{padding:38px 36px;border-top:1px solid var(--bdr);display:flex;justify-content:space-between;align-items:center;font-family:'Josefin Sans',sans-serif;font-size:12px;letter-spacing:.3em;color:rgba(216,235,216,.18);}
    @media(max-width:640px){
      .sr-hero{grid-template-columns:1fr;}.sr-right{display:none;}.sr-left{padding:96px 28px 56px;}
      .sr-spread{grid-template-columns:1fr;gap:24px;}.sr-rsvp-opts{flex-direction:column;}.sr-body{padding:0 24px;}
    }
  `;

  return (
    <>
      <style>{C}</style>

      {/* ── EDITORIAL HERO ── */}
      <div className="sr-hero">
        <div className="sr-left" style={{ animation:"srSlideLeft 1.2s ease both" }}>
          <div className="sr-issue">UNDANGAN PERNIKAHAN · {new Date().getFullYear()}</div>
          <div className="sr-vol">Vol. 01</div>
          <div className="sr-names">
            {wedding.groom_name}
            <span className="sr-amp">&amp;</span>
            <span className="sr-bride-name">{wedding.bride_name}</span>
          </div>
          {guest && <div className="sr-guest-label">Kepada · {guest.name}</div>}
          <div className="sr-date">{wc(wedding,'resepsi_date') || wc(wedding,'akad_date')}</div>
        </div>
        <div className="sr-right">
          <img src={coverPhoto} alt="" className="sr-cover" />
          <div className="sr-cover-overlay" />
          <div className="sr-caption">{wc(wedding,'venue_name')}</div>
        </div>
      </div>

      {/* ── KATA PEMBUKA ── */}
      {wc(wedding,'opening_text') && (
        <OpeningSection text={wc(wedding,'opening_text')} accentColor="#7A9E7E" textColor="rgba(240,232,216,.45)" borderColor="rgba(122,158,126,.12)" fontFamily="'Cormorant Garamond',serif" />
      )}
      <MusicPlayer url={wc(wedding,'music_url')} accentColor="#7A9E7E" dark={true} />

      {/* ── CONTENT ── */}
      <div style={{ background:"var(--bg)" }}>
        <div className="sr-body">

          {/* Events + Countdown */}
          <Rev>
            <div className="sr-spread">
              <div>
                <div className="sr-kicker">Waktu &amp; Tempat</div>
                <div className="sr-h2">Hari Istimewa Kami</div>
                <div className="sr-rule" />
                <div className="sr-ev">
                  <div className="sr-ev-type">Akad Nikah</div>
                  <div className="sr-ev-date">{wc(wedding,'akad_date')}</div>
                  <div className="sr-ev-time">{wc(wedding,'akad_time')}</div>
                  <div className="sr-ev-venue">{wc(wedding,'venue_name')} · {wc(wedding,'venue_address')}</div>
                </div>
                <div className="sr-ev">
                  <div className="sr-ev-type">Resepsi</div>
                  <div className="sr-ev-date">{wc(wedding,'resepsi_date')}</div>
                  <div className="sr-ev-time">{wc(wedding,'resepsi_time')}</div>
                  <div className="sr-ev-venue">{wc(wedding,'venue_name')} · {wc(wedding,'venue_address')}</div>
                </div>
                {wc(wedding,'venue_maps_url') && <a href={wc(wedding,'venue_maps_url')} target="_blank" rel="noopener" style={{ display:"inline-block", marginTop:10, fontFamily:"'Josefin Sans',sans-serif", fontSize:".43rem", letterSpacing:".22em", color:"var(--ac)", textDecoration:"none" }}>📍 Lihat Peta →</a>}
              </div>
              <div>
                <div className="sr-kicker">Menghitung Hari</div>
                <div className="sr-h2" style={{ fontSize:"1.4rem" }}>Countdown</div>
                <div className="sr-rule" />
                <div className="sr-cd">
                  {[["d","Hari"],["h","Jam"],["m","Menit"],["s","Detik"]].map(([k,l]) => (
                    <div key={k} className="sr-cd-item">
                      <div className="sr-cd-num">{String(countdown[k]).padStart(2,"0")}</div>
                      <div className="sr-cd-unit">{l}</div>
                    </div>
                  ))}
                </div>
                {wc(wedding,'dress_code') && <div style={{ marginTop:22, fontFamily:"'Josefin Sans',sans-serif", fontSize:".43rem", letterSpacing:".22em", color:"var(--txm)" }}>Dress Code · {wc(wedding,'dress_code')}</div>}
                <LiveStreamButton url={wc(wedding,'live_streaming_url')} accentColor="#7A9E7E" borderColor="rgba(122,158,126,.18)" />
              </div>
            </div>
          </Rev>

          {/* Love Story + Photo */}
          {wc(wedding,'love_story') && (
            <Rev d={0.1}>
              <div className="sr-spread">
                <div>
                  <div className="sr-kicker">Kisah Kami</div>
                  <div className="sr-h2">Perjalanan Cinta</div>
                  <div className="sr-rule" />
                  <div className="sr-pullquote">{wc(wedding,'love_story').slice(0,110)}…</div>
                  <p className="sr-body-text">{wc(wedding,'love_story')}</p>
                </div>
                {photos.length > 1 && (
                  <div>
                    <img src={photos[1]} alt="" className="sr-photo-tall" onClick={() => setLb(1)} />
                  </div>
                )}
              </div>
            </Rev>
          )}

          {/* Gallery */}
          {photos.length > 2 && (
            <Rev d={0.1}>
              <div className="sr-single">
                <div className="sr-kicker">Galeri Foto</div>
                <div className="sr-h2">Momen Berharga</div>
                <div className="sr-rule" />
                <div className="sr-photo-grid">
                  {photos.slice(2).map((p,i) => (
                    <img key={i} src={p} alt="" className="sr-photo-sq" onClick={() => setLb(i+2)} />
                  ))}
                </div>
              </div>
            </Rev>
          )}

          {/* RSVP + Guestbook */}
          <Rev>
            <div className="sr-spread">
              <div>
                <div className="sr-kicker">Konfirmasi Kehadiran</div>
                <div className="sr-h2">RSVP</div>
                <div className="sr-rule" />
                {rsvp.sent ? (
                  <p className="sr-body-text">✦ Terima kasih atas konfirmasimu.</p>
                ) : (
                  <>
                    <div className="sr-rsvp-opts">
                      {[["hadir","Hadir"],["mungkin","Mungkin"],["tidak_hadir","Tidak Hadir"]].map(([v,l]) => (
                        <div key={v} className={`sr-ropt${rsvp.rsvp===v?" sel":""}`} onClick={() => rsvp.setRsvp(v)}>{l}</div>
                      ))}
                    </div>
                    <textarea className="sr-inp" rows={3} placeholder="Ucapan..."
                      value={rsvp.msg} onChange={e => rsvp.setMsg(e.target.value)} style={{ resize:"none" }} />
                    <button className="sr-btn" onClick={rsvp.submit} disabled={!rsvp.rsvp}>Kirim Konfirmasi</button>
                  </>
                )}
              </div>
              <div>
                <div className="sr-kicker">Buku Tamu</div>
                <div className="sr-h2">Ucapan &amp; Doa</div>
                <div className="sr-rule" />
                <LiveFeedTicker weddingId={wedding.id} accentColor="#7A9E7E" textColor="rgba(240,232,216,0.65)" bgColor="rgba(122,158,126,0.04)" borderColor="rgba(122,158,126,0.1)" />
                {gb.sent ? (
                  <p className="sr-body-text">✦ Ucapanmu sudah terkirim.</p>
                ) : (
                  <>
                    <input className="sr-inp" placeholder="Nama..." value={gb.name} onChange={e => gb.setName(e.target.value)} />
                    <textarea className="sr-inp" rows={3} placeholder="Tulis ucapan..."
                      value={gb.msg} onChange={e => gb.setMsg(e.target.value)} style={{ resize:"none" }} />
                    <button className="sr-btn-ghost" onClick={gb.submit}>Kirim Ucapan</button>
                  </>
                )}
              </div>
            </div>
          </Rev>

          {/* Amplop */}
          {banks.length > 0 && (
            <Rev>
              <div className="sr-single">
                <div className="sr-kicker">Amplop Digital</div>
                <div className="sr-h2">Hadiah &amp; Doa</div>
                <div className="sr-rule" />
                <div style={{ maxWidth:480 }}>
                  {banks.map((b,i) => (
                    <div key={i} className="sr-amp-row">
                      <div>
                        <div className="sr-amp-bank">{b.bank||b.name}</div>
                        <div className="sr-amp-num">{b.number||b.account}</div>
                        <div className="sr-amp-holder">a.n. {b.holder}</div>
                      </div>
                      {copied===String(i)
                        ? <span style={{ fontFamily:"'Josefin Sans',sans-serif", fontSize:".43rem", color:"var(--ac)" }}>Disalin ✓</span>
                        : <button className="sr-btn-ghost" style={{ padding:"6px 14px", fontSize:".41rem" }}
                            onClick={() => copyNum(b.number||b.account, String(i))}>Salin</button>
                      }
                    </div>
                  ))}
                </div>
              </div>
            </Rev>
          )}

        </div>{/* /sr-body */}

        <div className="sr-footer">
          <span>✦ MYKISAH</span>
          <span>{wedding.groom_name} &amp; {wedding.bride_name}</span>
          <span>{new Date().getFullYear()}</span>
        </div>
      </div>

      {lb !== null && <LightboxModal photos={photos} startIndex={lb} onClose={() => setLb(null)} />}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// TEMA 3 — AMOUR  (polaroid film, dark rose, vintage)
// ═══════════════════════════════════════════════════════════════
export function ThemeAmour({ wedding, guest }) {
  const rsvp   = useRsvp(wedding, guest);
  const gb     = useGuestbook(wedding);
  const [lb, setLb]         = useState(null);
  const [copied, setCopied] = useState("");
  const countdown = useCountdown(wc(wedding,'target_date') || wedding.wedding_date);
  const photos = wcArr(wedding,'gallery').length ? wcArr(wedding,'gallery') : wcArr(wedding,'photos');
  const banks  = wcArr(wedding,'banks').length ? wcArr(wedding,'banks') : wcArr(wedding,'bank_accounts');
  const TILTS  = [-3.2, 2.4, -1.8, 3.1, -2.2, 1.5, -2.8, 2.7];

  function copyNum(num, key) {
    navigator.clipboard.writeText(num.replace(/\s/g,""));
    setCopied(key); setTimeout(() => setCopied(""), 2000);
  }

  const C = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;1,400&family=Tenor+Sans&display=swap');
    *{margin:0;padding:0;box-sizing:border-box;}
    :root{--bg:#120608;--bg2:#0E0407;--ac:#C97B6F;--acl:#E8AFA5;--tx:rgba(240,224,220,0.9);--txm:rgba(240,224,220,0.38);--bdr:rgba(201,123,111,0.12);}
    body{background:var(--bg);color:var(--tx);font-family:'Cormorant Garamond',serif;overflow-x:hidden;}
    @keyframes amFadeUp{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
    @keyframes amTilt{from{opacity:0;transform:rotate(var(--t)) scale(0.88)}to{opacity:1;transform:rotate(var(--t)) scale(1)}}
    /* hero */
    .am-hero{min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:80px 28px;text-align:center;position:relative;overflow:hidden;background:radial-gradient(ellipse 80% 65% at 50% 38%,#1A0810,var(--bg) 68%);}
    .am-filmtop{position:absolute;top:0;left:0;right:0;height:26px;background:repeating-linear-gradient(90deg,transparent,transparent 18px,rgba(201,123,111,.07) 18px,rgba(201,123,111,.07) 20px);opacity:.7;}
    .am-filmbot{position:absolute;bottom:0;left:0;right:0;height:26px;background:repeating-linear-gradient(90deg,transparent,transparent 18px,rgba(201,123,111,.07) 18px,rgba(201,123,111,.07) 20px);opacity:.7;}
    .am-tag{font-family:'Tenor Sans',sans-serif;font-size:13px;letter-spacing:.52em;color:rgba(201,123,111,.58);margin-bottom:30px;animation:amFadeUp 1s ease both;}
    .am-guest{font-family:'Tenor Sans',sans-serif;font-size:13px;letter-spacing:.22em;color:rgba(201,123,111,.48);margin-bottom:10px;animation:amFadeUp 1s ease .1s both;}
    .am-couple{font-family:'Cormorant Garamond',serif;font-size:clamp(3.2rem,12vw,6.8rem);line-height:.86;font-style:italic;animation:amFadeUp 1.2s ease .18s both;}
    .am-bride{color:var(--ac);}
    .am-amp{display:block;font-family:'Tenor Sans',sans-serif;font-size:.16em;letter-spacing:.5em;opacity:.14;margin:13px 0;font-style:normal;}
    .am-date{font-family:'Tenor Sans',sans-serif;font-size:13px;letter-spacing:.34em;color:var(--txm);margin-top:26px;animation:amFadeUp 1s ease .44s both;}
    /* polaroid section */
    .am-pol-sec{padding:72px 28px;background:var(--bg2);}
    .am-pol-title{font-family:'Tenor Sans',sans-serif;font-size:12px;letter-spacing:.5em;color:rgba(201,123,111,.55);text-align:center;margin-bottom:46px;}
    .am-pol-scatter{display:flex;flex-wrap:wrap;justify-content:center;gap:20px;max-width:740px;margin:0 auto;}
    .am-polaroid{background:#F5EDE8;padding:9px 9px 36px;cursor:pointer;transition:transform .35s,box-shadow .35s;transform:rotate(var(--t));box-shadow:0 6px 28px rgba(0,0,0,.45);}
    .am-polaroid:hover{box-shadow:0 14px 44px rgba(0,0,0,.6);transform:rotate(calc(var(--t) * 0.5)) scale(1.04);}
    .am-polaroid img{width:190px;height:190px;object-fit:cover;display:block;filter:sepia(.14) contrast(.96);}
    .am-pol-caption{font-family:'Cormorant Garamond',serif;font-size:15px;font-style:italic;color:#8B6055;text-align:center;margin-top:9px;padding:0 3px;}
    /* sections */
    .am-sec{padding:76px 28px;max-width:580px;margin:0 auto;}
    .am-label{font-family:'Tenor Sans',sans-serif;font-size:12px;letter-spacing:.5em;color:rgba(201,123,111,.58);text-align:center;margin-bottom:9px;}
    .am-h2{font-family:'Cormorant Garamond',serif;font-size:clamp(1.7rem,5.5vw,2.8rem);font-style:italic;text-align:center;margin-bottom:22px;}
    .am-rule{width:34px;height:1px;background:rgba(201,123,111,.18);margin:0 auto 26px;}
    /* event */
    .am-ev{padding:22px;border:1px solid var(--bdr);margin-bottom:8px;background:rgba(201,123,111,.02);}
    .am-ev-type{font-family:'Tenor Sans',sans-serif;font-size:12px;letter-spacing:.4em;color:var(--ac);margin-bottom:9px;}
    .am-ev-date{font-size:16px;font-style:italic;margin-bottom:3px;}
    .am-ev-time{font-family:'Tenor Sans',sans-serif;font-size:13px;letter-spacing:.16em;color:var(--txm);margin-bottom:8px;}
    .am-ev-venue{font-size:17px;color:var(--txm);line-height:1.85;}
    /* countdown */
    .am-cd{display:flex;border-top:1px solid var(--bdr);}
    .am-cd-item{flex:1;text-align:center;border-right:1px solid var(--bdr);padding:14px 6px;}
    .am-cd-item:last-child{border-right:none;}
    .am-cd-num{font-family:'Cormorant Garamond',serif;font-size:clamp(1.8rem,7vw,3rem);color:var(--ac);line-height:1;}
    .am-cd-unit{font-family:'Tenor Sans',sans-serif;font-size:12px;letter-spacing:.24em;color:var(--txm);margin-top:5px;}
    /* love */
    .am-love{font-size:16px;line-height:2.25;color:var(--txm);font-style:italic;white-space:pre-line;}
    /* rsvp */
    .am-rsvp-opts{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:13px;}
    .am-ropt{padding:14px 6px;border:1px solid var(--bdr);cursor:pointer;text-align:center;font-family:'Tenor Sans',sans-serif;font-size:12px;letter-spacing:.13em;transition:all .25s;}
    .am-ropt:hover,.am-ropt.sel{border-color:var(--ac);background:rgba(201,123,111,.05);color:var(--ac);}
    .am-ricon{font-size:1.05rem;display:block;margin-bottom:5px;}
    .am-inp{width:100%;padding:11px 12px;background:rgba(255,255,255,.02);border:1px solid var(--bdr);color:var(--tx);font-family:'Cormorant Garamond',serif;font-size:16px;font-style:italic;outline:none;resize:none;transition:border-color .25s;margin-bottom:10px;}
    .am-inp:focus{border-color:var(--ac);}
    .am-inp::placeholder{color:var(--txm);}
    .am-btn{width:100%;padding:13px;background:var(--ac);color:var(--bg);font-family:'Tenor Sans',sans-serif;font-size:13px;letter-spacing:.24em;border:none;cursor:pointer;transition:opacity .2s;}
    .am-btn:hover{opacity:.84;}
    .am-btn:disabled{opacity:.4;cursor:default;}
    .am-btn-ghost{padding:10px 20px;background:transparent;border:1px solid var(--bdr);color:var(--ac);font-family:'Tenor Sans',sans-serif;font-size:12px;letter-spacing:.2em;cursor:pointer;transition:all .25s;}
    .am-btn-ghost:hover{background:rgba(201,123,111,.05);}
    /* amplop */
    .am-amp-card{padding:17px;background:rgba(201,123,111,.03);border:1px solid var(--bdr);display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;}
    .am-amp-bank{font-family:'Tenor Sans',sans-serif;font-size:12px;letter-spacing:.2em;color:var(--ac);}
    .am-amp-num{font-size:16px;font-style:italic;margin:4px 0;}
    .am-amp-holder{font-size:15px;color:var(--txm);}
    /* footer */
    .am-footer{padding:34px;text-align:center;border-top:1px solid var(--bdr);}
    @media(max-width:540px){.am-rsvp-opts{grid-template-columns:1fr;}}
  `;

  return (
    <>
      <style>{C}</style>

      {/* ── FILM HERO ── */}
      <section className="am-hero">
        <div className="am-filmtop" /><div className="am-filmbot" />
        <div className="am-tag">✦ UNDANGAN PERNIKAHAN ✦</div>
        {guest && <div className="am-guest">Kepada · {guest.name}</div>}
        <div className="am-couple">
          {wedding.groom_name}
          <span className="am-amp">&amp;</span>
          <span className="am-bride">{wedding.bride_name}</span>
        </div>
        <div className="am-date">{wc(wedding,'resepsi_date') || wc(wedding,'akad_date')}</div>
      </section>

      {/* ── KATA PEMBUKA ── */}
      {wc(wedding,'opening_text') && (
        <OpeningSection text={wc(wedding,'opening_text')} accentColor="#C97B6F" textColor="rgba(240,232,216,.45)" borderColor="rgba(201,123,111,.12)" fontFamily="'Cormorant Garamond',serif" />
      )}
      <MusicPlayer url={wc(wedding,'music_url')} accentColor="#C97B6F" dark={true} />

      {/* ── POLAROID GALLERY ── */}
      {photos.length > 0 && (
        <div className="am-pol-sec">
          <Rev><div className="am-pol-title">✦ GALERI FOTO ✦</div></Rev>
          <div className="am-pol-scatter">
            {photos.map((p,i) => (
              <Rev key={i} d={i * 0.08}>
                <div className="am-polaroid"
                  style={{ "--t": `${TILTS[i % TILTS.length]}deg` }}
                  onClick={() => setLb(i)}>
                  <img src={p} alt="" />
                  <div className="am-pol-caption">{i === 0 ? `${wedding.groom_name} & ${wedding.bride_name}` : "✦"}</div>
                </div>
              </Rev>
            ))}
          </div>
        </div>
      )}

      {/* ── COUNTDOWN ── */}
      <div className="am-sec">
        <Rev>
          <div className="am-label">Menghitung Hari</div>
          <div className="am-h2">Waktu Pernikahan</div>
          <div className="am-rule" />
          <div className="am-cd">
            {[["d","Hari"],["h","Jam"],["m","Menit"],["s","Detik"]].map(([k,l]) => (
              <div key={k} className="am-cd-item">
                <div className="am-cd-num">{String(countdown[k]).padStart(2,"0")}</div>
                <div className="am-cd-unit">{l}</div>
              </div>
            ))}
          </div>
        </Rev>
      </div>

      {/* ── EVENTS ── */}
      <div className="am-sec" style={{ paddingTop:0 }}>
        <Rev>
          <div className="am-label">Waktu &amp; Tempat</div>
          <div className="am-h2">Hari Istimewa</div>
          <div className="am-rule" />
          <div className="am-ev"><div className="am-ev-type">Akad Nikah</div><div className="am-ev-date">{wc(wedding,'akad_date')}</div><div className="am-ev-time">{wc(wedding,'akad_time')}</div><div className="am-ev-venue">{wc(wedding,'venue_name')} · {wc(wedding,'venue_address')}</div></div>
          <div className="am-ev"><div className="am-ev-type">Resepsi</div><div className="am-ev-date">{wc(wedding,'resepsi_date')}</div><div className="am-ev-time">{wc(wedding,'resepsi_time')}</div><div className="am-ev-venue">{wc(wedding,'venue_name')} · {wc(wedding,'venue_address')}</div></div>
          {wc(wedding,'venue_maps_url') && <a href={wc(wedding,'venue_maps_url')} target="_blank" rel="noopener" style={{ display:"inline-block", marginTop:10, fontFamily:"'Tenor Sans',sans-serif", fontSize:".43rem", letterSpacing:".2em", color:"var(--ac)", textDecoration:"none" }}>📍 Lihat Peta →</a>}
          {wc(wedding,'dress_code') && <div style={{ marginTop:16, fontFamily:"'Tenor Sans',sans-serif", fontSize:".43rem", letterSpacing:".2em", color:"var(--txm)" }}>Dress Code · {wc(wedding,'dress_code')}</div>}
          <LiveStreamButton url={wc(wedding,'live_streaming_url')} accentColor="#C97B6F" borderColor="rgba(201,123,111,.18)" />
        </Rev>
      </div>

      {/* ── LOVE STORY ── */}
      {wc(wedding,'love_story') && (
        <div style={{ background:"var(--bg2)", padding:"76px 28px" }}>
          <div style={{ maxWidth:560, margin:"0 auto" }}>
            <Rev>
              <div className="am-label">Kisah Kami</div>
              <div className="am-h2">Perjalanan Cinta</div>
              <div className="am-rule" />
              <p className="am-love">{wc(wedding,'love_story')}</p>
            </Rev>
          </div>
        </div>
      )}

      {/* ── RSVP ── */}
      <div className="am-sec">
        <Rev>
          <div className="am-label">Konfirmasi</div>
          <div className="am-h2">RSVP</div>
          <div className="am-rule" />
          {rsvp.sent ? <div style={{ textAlign:"center", fontStyle:"italic", color:"rgba(201,123,111,.68)" }}>✦ Terima kasih atas konfirmasimu.</div> : (
            <>
              <div className="am-rsvp-opts">
                {[["hadir","✅","Hadir"],["mungkin","🤔","Mungkin"],["tidak_hadir","❌","Tidak Hadir"]].map(([v,ic,l]) => (
                  <div key={v} className={`am-ropt${rsvp.rsvp===v?" sel":""}`} onClick={() => rsvp.setRsvp(v)}>
                    <span className="am-ricon">{ic}</span>{l}
                  </div>
                ))}
              </div>
              <textarea className="am-inp" rows={3} placeholder="Ucapan..." value={rsvp.msg} onChange={e => rsvp.setMsg(e.target.value)} />
              <button className="am-btn" onClick={rsvp.submit} disabled={!rsvp.rsvp}>✦ Kirim Konfirmasi</button>
            </>
          )}
        </Rev>
      </div>

      {/* ── GUESTBOOK ── */}
      <div style={{ background:"var(--bg2)", padding:"76px 28px" }}>
        <div style={{ maxWidth:580, margin:"0 auto" }}>
          <Rev>
            <div className="am-label">Buku Tamu</div>
            <div className="am-h2">Ucapan &amp; Doa</div>
            <div className="am-rule" />
            <LiveFeedTicker weddingId={wedding.id} accentColor="#C97B6F" textColor="rgba(240,232,216,0.65)" bgColor="rgba(201,123,111,0.04)" borderColor="rgba(201,123,111,0.12)" />
            {gb.sent ? <div style={{ fontStyle:"italic", color:"rgba(201,123,111,.58)" }}>✦ Ucapanmu sudah terkirim.</div> : (
              <>
                <input className="am-inp" placeholder="Nama..." value={gb.name} onChange={e => gb.setName(e.target.value)} />
                <textarea className="am-inp" rows={3} placeholder="Tulis ucapan..." value={gb.msg} onChange={e => gb.setMsg(e.target.value)} />
                <button className="am-btn-ghost" onClick={gb.submit}>✦ Kirim Ucapan</button>
              </>
            )}
          </Rev>
        </div>
      </div>

      {/* ── AMPLOP ── */}
      {banks.length > 0 && (
        <div className="am-sec">
          <Rev>
            <div className="am-label">Amplop Digital</div>
            <div className="am-h2">Hadiah &amp; Doa</div>
            <div className="am-rule" />
            {banks.map((b,i) => (
              <div key={i} className="am-amp-card">
                <div><div className="am-amp-bank">{b.bank||b.name}</div><div className="am-amp-num">{b.number||b.account}</div><div className="am-amp-holder">a.n. {b.holder}</div></div>
                {copied===String(i)
                  ? <span style={{ fontFamily:"'Tenor Sans',sans-serif", fontSize:".43rem", color:"var(--ac)" }}>Disalin ✓</span>
                  : <button className="am-btn-ghost" style={{ padding:"6px 14px" }} onClick={() => copyNum(b.number||b.account, String(i))}>Salin</button>
                }
              </div>
            ))}
          </Rev>
        </div>
      )}

      <div className="am-footer">
        <div style={{ fontFamily:"'Tenor Sans',sans-serif", fontSize:".48rem", letterSpacing:".42em", color:"rgba(201,123,111,.22)" }}>✦ MYKISAH ✦</div>
      </div>

      {lb !== null && <LightboxModal photos={photos} startIndex={lb} onClose={() => setLb(null)} />}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// TEMA 4 — TWILIGHT  (asymmetric modern, dark purple)
// ═══════════════════════════════════════════════════════════════
export function ThemeTwilight({ wedding, guest }) {
  const rsvp   = useRsvp(wedding, guest);
  const gb     = useGuestbook(wedding);
  const [lb, setLb]         = useState(null);
  const [copied, setCopied] = useState("");
  const countdown = useCountdown(wc(wedding,'target_date') || wedding.wedding_date);
  const photos = wcArr(wedding,'gallery').length ? wcArr(wedding,'gallery') : wcArr(wedding,'photos');
  const banks  = wcArr(wedding,'banks').length ? wcArr(wedding,'banks') : wcArr(wedding,'bank_accounts');

  function copyNum(num, key) {
    navigator.clipboard.writeText(num.replace(/\s/g,""));
    setCopied(key); setTimeout(() => setCopied(""), 2000);
  }

  const C = `
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;1,400&family=Outfit:wght@200;300;400&display=swap');
    *{margin:0;padding:0;box-sizing:border-box;}
    :root{--bg:#08070F;--bg2:#0F0D1C;--ac:#9B87B8;--acl:#BFB0D8;--tx:rgba(224,220,240,0.88);--txm:rgba(224,220,240,0.36);--bdr:rgba(155,135,184,0.11);}
    body{background:var(--bg);color:var(--tx);font-family:'Outfit',sans-serif;font-weight:300;overflow-x:hidden;}
    @keyframes twSlideL{from{opacity:0;transform:translateX(-36px)}to{opacity:1;transform:translateX(0)}}
    @keyframes twFadeUp{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
    /* ASYMMETRIC HERO */
    .tw-hero{min-height:100vh;position:relative;overflow:hidden;display:flex;align-items:flex-end;padding:56px 44px;}
    .tw-hero-bg{position:absolute;inset:0;background:linear-gradient(140deg,var(--bg2) 0%,var(--bg) 55%);}
    .tw-hero-glow{position:absolute;top:-15%;right:-8%;width:66%;height:115%;background:radial-gradient(ellipse,rgba(155,135,184,.07) 0%,transparent 62%);pointer-events:none;}
    .tw-hero-vline{position:absolute;top:0;left:54%;width:1px;height:100%;background:linear-gradient(to bottom,transparent,rgba(155,135,184,.13),transparent);}
    .tw-hero-year{position:absolute;bottom:-24px;right:-4px;font-family:'Outfit',sans-serif;font-size:clamp(7rem,22vw,17rem);font-weight:200;line-height:.85;color:rgba(155,135,184,.05);letter-spacing:-.06em;pointer-events:none;user-select:none;}
    .tw-hero-content{position:relative;z-index:2;max-width:860px;width:100%;}
    .tw-tag{font-size:13px;letter-spacing:.54em;color:rgba(155,135,184,.48);margin-bottom:18px;animation:twFadeUp 1s ease both;}
    .tw-groom{font-family:'Playfair Display',serif;font-size:clamp(2.8rem,10vw,6.4rem);line-height:.86;animation:twSlideL 1.2s ease .1s both;}
    .tw-amp{font-size:.2em;font-family:'Outfit',sans-serif;font-weight:200;opacity:.13;display:block;letter-spacing:.42em;margin:9px 0;}
    .tw-bride{font-family:'Playfair Display',serif;font-size:clamp(2.8rem,10vw,6.4rem);line-height:.86;color:var(--ac);font-style:italic;animation:twSlideL 1.2s ease .18s both;}
    .tw-meta{display:flex;gap:28px;margin-top:26px;flex-wrap:wrap;animation:twFadeUp 1.1s ease .32s both;}
    .tw-meta-item{font-size:15px;letter-spacing:.14em;color:var(--txm);}
    .tw-meta-item strong{display:block;color:rgba(224,220,240,.7);font-weight:300;margin-bottom:2px;}
    .tw-scroll-mark{position:absolute;bottom:30px;right:44px;display:flex;flex-direction:column;align-items:center;gap:7px;animation:twFadeUp 1.2s ease .6s both;}
    .tw-scroll-num{font-size:11px;letter-spacing:.3em;color:rgba(155,135,184,.28);}
    .tw-scroll-line{width:1px;height:44px;background:linear-gradient(to bottom,var(--ac),transparent);}
    /* blocks */
    .tw-block{padding:80px 44px;}
    .tw-block-alt{padding:80px 44px;background:var(--bg2);}
    .tw-inner{max-width:880px;margin:0 auto;}
    .tw-label{font-size:12px;letter-spacing:.54em;color:rgba(155,135,184,.52);margin-bottom:10px;}
    .tw-h2{font-family:'Playfair Display',serif;font-size:clamp(1.9rem,5.5vw,3.1rem);margin-bottom:18px;}
    .tw-rule{width:34px;height:1px;background:var(--bdr);margin-bottom:26px;}
    /* ASYMMETRIC EVENT — 60/40 split */
    .tw-ev-wrap{display:grid;grid-template-columns:58% 42%;gap:0;}
    .tw-ev-main{padding-right:38px;border-right:1px solid var(--bdr);}
    .tw-ev-side{padding-left:30px;display:flex;flex-direction:column;justify-content:center;}
    .tw-ev-type{font-size:12px;letter-spacing:.44em;color:var(--ac);margin-bottom:9px;}
    .tw-ev-date{font-family:'Playfair Display',serif;font-size:1.35rem;font-style:italic;margin-bottom:3px;}
    .tw-ev-time{font-size:14px;letter-spacing:.17em;color:var(--txm);margin-bottom:9px;}
    .tw-ev-venue{font-size:17px;color:var(--txm);line-height:1.88;}
    .tw-divider{height:1px;background:var(--bdr);margin:28px 0;}
    /* countdown */
    .tw-cd{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:var(--bdr);border:1px solid var(--bdr);}
    .tw-cd-item{background:var(--bg);padding:18px 7px;text-align:center;}
    .tw-cd-num{font-family:'Playfair Display',serif;font-size:clamp(1.8rem,6vw,2.9rem);color:var(--ac);}
    .tw-cd-unit{font-size:12px;letter-spacing:.28em;color:var(--txm);margin-top:5px;}
    /* gallery 3/2 asymmetric */
    .tw-gal{display:grid;grid-template-columns:1.4fr 1fr;grid-template-rows:1fr 1fr;gap:3px;aspect-ratio:16/10;}
    .tw-gal-main{grid-row:span 2;overflow:hidden;}
    .tw-gal-sm{overflow:hidden;}
    .tw-gal-img{width:100%;height:100%;object-fit:cover;cursor:pointer;transition:transform .5s,filter .4s;filter:brightness(.78);}
    .tw-gal-img:hover{transform:scale(1.04);filter:brightness(.95);}
    /* love */
    .tw-love{font-size:16px;line-height:2.2;color:var(--txm);white-space:pre-line;}
    /* rsvp + guestbook side by side */
    .tw-two-col{display:grid;grid-template-columns:1fr 1fr;gap:48px;}
    .tw-rsvp-opts{display:flex;gap:1px;background:var(--bdr);margin-bottom:14px;}
    .tw-ropt{flex:1;padding:13px 6px;background:var(--bg);cursor:pointer;text-align:center;font-size:12px;letter-spacing:.15em;color:var(--txm);transition:all .25s;}
    .tw-ropt:hover,.tw-ropt.sel{background:var(--bg2);color:var(--ac);}
    .tw-inp{width:100%;padding:10px 12px;background:rgba(155,135,184,.03);border:1px solid var(--bdr);color:var(--tx);font-family:'Outfit',sans-serif;font-size:16px;font-weight:300;outline:none;resize:none;transition:border-color .25s;margin-bottom:9px;}
    .tw-inp:focus{border-color:var(--ac);}
    .tw-inp::placeholder{color:var(--txm);}
    .tw-btn{padding:11px 26px;background:var(--ac);color:var(--bg);font-size:13px;letter-spacing:.22em;border:none;cursor:pointer;transition:opacity .2s;}
    .tw-btn:hover{opacity:.85;}
    .tw-btn:disabled{opacity:.4;cursor:default;}
    .tw-btn-ghost{padding:10px 20px;background:transparent;border:1px solid var(--bdr);color:var(--ac);font-size:12px;letter-spacing:.2em;cursor:pointer;transition:all .25s;}
    .tw-btn-ghost:hover{background:rgba(155,135,184,.05);}
    /* amplop */
    .tw-amp-row{display:flex;justify-content:space-between;align-items:center;padding:14px 0;border-bottom:1px solid var(--bdr);}
    .tw-amp-bank{font-size:12px;letter-spacing:.22em;color:var(--ac);}
    .tw-amp-num{font-family:'Playfair Display',serif;font-size:16px;margin:3px 0;}
    .tw-amp-holder{font-size:16px;color:var(--txm);}
    .tw-love-long{font-size:16px;line-height:2.2;color:var(--txm);white-space:pre-line;}
    /* footer */
    .tw-footer{padding:34px 44px;border-top:1px solid var(--bdr);display:flex;justify-content:space-between;align-items:center;font-size:12px;letter-spacing:.3em;color:rgba(224,220,240,.14);}
    @media(max-width:640px){
      .tw-ev-wrap{grid-template-columns:1fr;}.tw-ev-main{padding-right:0;border-right:none;border-bottom:1px solid var(--bdr);padding-bottom:22px;}.tw-ev-side{padding-left:0;padding-top:22px;}.tw-gal{grid-template-columns:1fr;}.tw-gal-main{grid-row:span 1;}.tw-two-col{grid-template-columns:1fr;gap:24px;}.tw-block,.tw-block-alt{padding:64px 24px;}.tw-hero{padding:96px 24px 56px;}.tw-meta{gap:14px;}
    }
  `;

  const year = (wc(wedding,'target_date') || wedding.wedding_date) ? new Date(wc(wedding,'target_date') || wedding.wedding_date).getFullYear() : new Date().getFullYear();

  return (
    <>
      <style>{C}</style>

      {/* ── ASYMMETRIC HERO ── */}
      <section className="tw-hero">
        <div className="tw-hero-bg" /><div className="tw-hero-glow" /><div className="tw-hero-vline" />
        <div className="tw-hero-year">{year}</div>
        <div className="tw-hero-content">
          <div className="tw-tag">✦ UNDANGAN PERNIKAHAN</div>
          <div className="tw-groom">{wedding.groom_name}</div>
          <span className="tw-amp">&amp;</span>
          <div className="tw-bride">{wedding.bride_name}</div>
          <div className="tw-meta">
            <div className="tw-meta-item"><strong>{wc(wedding,'resepsi_date') || wc(wedding,'akad_date')}</strong>Tanggal</div>
            <div className="tw-meta-item"><strong>{wc(wedding,'venue_name')}</strong>Lokasi</div>
            {guest && <div className="tw-meta-item"><strong>{guest.name}</strong>Tamu</div>}
          </div>
        </div>
        <div className="tw-scroll-mark">
          <div className="tw-scroll-num">01</div>
          <div className="tw-scroll-line" />
        </div>
      </section>

      {/* ── KATA PEMBUKA ── */}
      {wc(wedding,'opening_text') && (
        <OpeningSection text={wc(wedding,'opening_text')} accentColor="#9B87B8" textColor="rgba(224,220,240,.42)" borderColor="rgba(155,135,184,.12)" fontFamily="'Cormorant Garamond',serif" />
      )}
      <MusicPlayer url={wc(wedding,'music_url')} accentColor="#9B87B8" dark={true} />

      {/* ── EVENTS + COUNTDOWN ── */}
      <div className="tw-block">
        <div className="tw-inner">
          <Rev>
            <div className="tw-label">Waktu &amp; Tempat</div>
            <div className="tw-h2">Hari Istimewa</div>
            <div className="tw-rule" />
            <div className="tw-ev-wrap">
              <div className="tw-ev-main">
                <div className="tw-ev-type">Akad Nikah</div>
                <div className="tw-ev-date">{wc(wedding,'akad_date')}</div>
                <div className="tw-ev-time">{wc(wedding,'akad_time')}</div>
                <div className="tw-ev-venue">{wc(wedding,'venue_name')}<br />{wc(wedding,'venue_address')}</div>
                <div className="tw-divider" />
                <div className="tw-ev-type">Resepsi</div>
                <div className="tw-ev-date">{wc(wedding,'resepsi_date')}</div>
                <div className="tw-ev-time">{wc(wedding,'resepsi_time')}</div>
                <div className="tw-ev-venue">{wc(wedding,'venue_name')}<br />{wc(wedding,'venue_address')}</div>
                {wc(wedding,'venue_maps_url') && <a href={wc(wedding,'venue_maps_url')} target="_blank" rel="noopener" style={{ display:"inline-block", marginTop:14, fontSize:".43rem", letterSpacing:".2em", color:"var(--ac)", textDecoration:"none" }}>📍 Lihat Peta →</a>}
              </div>
              <div className="tw-ev-side">
                <div className="tw-label" style={{ marginBottom:14 }}>Menghitung Hari</div>
                <div className="tw-cd">
                  {[["d","Hari"],["h","Jam"],["m","Menit"],["s","Detik"]].map(([k,l]) => (
                    <div key={k} className="tw-cd-item">
                      <div className="tw-cd-num">{String(countdown[k]).padStart(2,"0")}</div>
                      <div className="tw-cd-unit">{l}</div>
                    </div>
                  ))}
                </div>
                {wc(wedding,'dress_code') && <div style={{ marginTop:16, fontSize:".43rem", letterSpacing:".16em", color:"var(--txm)" }}>Dress Code · {wc(wedding,'dress_code')}</div>}
                <LiveStreamButton url={wc(wedding,'live_streaming_url')} accentColor="#9B87B8" borderColor="rgba(155,135,184,.18)" />
              </div>
            </div>
          </Rev>
        </div>
      </div>

      {/* ── GALLERY ── */}
      {photos.length > 0 && (
        <div className="tw-block-alt">
          <div className="tw-inner">
            <Rev>
              <div className="tw-label">Galeri Foto</div>
              <div className="tw-h2">Momen Berharga</div>
              <div className="tw-rule" />
              <div className="tw-gal">
                {photos.slice(0,1).map((p,i) => <div key={i} className="tw-gal-main"><img src={p} alt="" className="tw-gal-img" onClick={() => setLb(i)} /></div>)}
                {photos.slice(1,3).map((p,i) => <div key={i+1} className="tw-gal-sm"><img src={p} alt="" className="tw-gal-img" onClick={() => setLb(i+1)} /></div>)}
              </div>
            </Rev>
          </div>
        </div>
      )}

      {/* ── LOVE STORY ── */}
      {wc(wedding,'love_story') && (
        <div className="tw-block">
          <div className="tw-inner" style={{ maxWidth:560 }}>
            <Rev>
              <div className="tw-label">Kisah Kami</div>
              <div className="tw-h2">Perjalanan Cinta</div>
              <div className="tw-rule" />
              <p className="tw-love-long">{wc(wedding,'love_story')}</p>
            </Rev>
          </div>
        </div>
      )}

      {/* ── RSVP + GUESTBOOK ── */}
      <div className="tw-block-alt">
        <div className="tw-inner">
          <div className="tw-two-col">
            <Rev>
              <div className="tw-label">Konfirmasi</div>
              <div className="tw-h2" style={{ fontSize:"1.75rem" }}>RSVP</div>
              <div className="tw-rule" />
              {rsvp.sent ? <div style={{ color:"rgba(155,135,184,.7)", fontSize:".9rem" }}>✦ Terima kasih.</div> : (
                <>
                  <div className="tw-rsvp-opts">
                    {[["hadir","Hadir"],["mungkin","Mungkin"],["tidak_hadir","Tidak"]].map(([v,l]) => (
                      <div key={v} className={`tw-ropt${rsvp.rsvp===v?" sel":""}`} onClick={() => rsvp.setRsvp(v)}>{l}</div>
                    ))}
                  </div>
                  <textarea className="tw-inp" rows={3} placeholder="Ucapan..." value={rsvp.msg} onChange={e => rsvp.setMsg(e.target.value)} />
                  <button className="tw-btn" onClick={rsvp.submit} disabled={!rsvp.rsvp}>Kirim</button>
                </>
              )}
            </Rev>
            <Rev d={0.1}>
              <div className="tw-label">Buku Tamu</div>
              <div className="tw-h2" style={{ fontSize:"1.75rem" }}>Ucapan</div>
              <div className="tw-rule" />
              <LiveFeedTicker weddingId={wedding.id} accentColor="#9B87B8" textColor="rgba(240,232,216,0.65)" bgColor="rgba(155,135,184,0.04)" borderColor="rgba(155,135,184,0.12)" />
              {gb.sent ? <div style={{ color:"rgba(155,135,184,.6)", fontSize:".9rem" }}>✦ Terkirim.</div> : (
                <>
                  <input className="tw-inp" placeholder="Nama..." value={gb.name} onChange={e => gb.setName(e.target.value)} />
                  <textarea className="tw-inp" rows={3} placeholder="Tulis ucapan..." value={gb.msg} onChange={e => gb.setMsg(e.target.value)} />
                  <button className="tw-btn-ghost" onClick={gb.submit}>Kirim Ucapan</button>
                </>
              )}
            </Rev>
          </div>
        </div>
      </div>

      {/* ── AMPLOP ── */}
      {banks.length > 0 && (
        <div className="tw-block">
          <div className="tw-inner" style={{ maxWidth:500 }}>
            <Rev>
              <div className="tw-label">Amplop Digital</div>
              <div className="tw-h2">Hadiah &amp; Doa</div>
              <div className="tw-rule" />
              {banks.map((b,i) => (
                <div key={i} className="tw-amp-row">
                  <div><div className="tw-amp-bank">{b.bank||b.name}</div><div className="tw-amp-num">{b.number||b.account}</div><div className="tw-amp-holder">a.n. {b.holder}</div></div>
                  {copied===String(i)
                    ? <span style={{ fontSize:".43rem", color:"var(--ac)" }}>Disalin ✓</span>
                    : <button className="tw-btn-ghost" style={{ padding:"6px 14px" }} onClick={() => copyNum(b.number||b.account, String(i))}>Salin</button>
                  }
                </div>
              ))}
            </Rev>
          </div>
        </div>
      )}

      <div className="tw-footer">
        <span>✦ MYKISAH</span>
        <span>{wedding.groom_name} &amp; {wedding.bride_name}</span>
        <span>{new Date().getFullYear()}</span>
      </div>

      {lb !== null && <LightboxModal photos={photos} startIndex={lb} onClose={() => setLb(null)} />}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// TEMA 5 — IVORY  (klasik simetris, light, ornamental)
// ═══════════════════════════════════════════════════════════════
export function ThemeIvory({ wedding, guest }) {
  const rsvp   = useRsvp(wedding, guest);
  const gb     = useGuestbook(wedding);
  const [lb, setLb]         = useState(null);
  const [copied, setCopied] = useState("");
  const countdown = useCountdown(wc(wedding,'target_date') || wedding.wedding_date);
  const photos = wcArr(wedding,'gallery').length ? wcArr(wedding,'gallery') : wcArr(wedding,'photos');
  const banks  = wcArr(wedding,'banks').length ? wcArr(wedding,'banks') : wcArr(wedding,'bank_accounts');

  function copyNum(num, key) {
    navigator.clipboard.writeText(num.replace(/\s/g,""));
    setCopied(key); setTimeout(() => setCopied(""), 2000);
  }

  const C = `
    @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;1,400&family=Cormorant+SC:wght@400;500&display=swap');
    *{margin:0;padding:0;box-sizing:border-box;}
    :root{--bg:#F5F0E8;--bg2:#EDE5D8;--ac:#8B6F47;--acl:#B09068;--tx:rgba(44,36,22,0.9);--txm:rgba(44,36,22,0.42);--bdr:rgba(139,111,71,0.14);}
    body{background:var(--bg);color:var(--tx);font-family:'EB Garamond',serif;overflow-x:hidden;}
    @keyframes ivFadeUp{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}}
    /* HERO */
    .iv-hero{min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:80px 44px;position:relative;}
    .iv-frame{position:absolute;inset:22px;border:1px solid rgba(139,111,71,.14);pointer-events:none;}
    .iv-frame::before{content:'';position:absolute;inset:7px;border:1px solid rgba(139,111,71,.06);}
    .iv-corner{position:absolute;font-size:1.15rem;color:rgba(139,111,71,.32);line-height:1;}
    .iv-tl{top:14px;left:14px;} .iv-tr{top:14px;right:14px;} .iv-bl{bottom:14px;left:14px;} .iv-br{bottom:14px;right:14px;}
    .iv-tag{font-family:'Cormorant SC',serif;font-size:13px;letter-spacing:.54em;color:rgba(139,111,71,.56);margin-bottom:30px;animation:ivFadeUp 1s ease both;}
    .iv-ornament{font-size:1.15rem;color:rgba(139,111,71,.22);margin-bottom:18px;animation:ivFadeUp 1s ease .08s both;}
    .iv-guest{font-family:'EB Garamond',serif;font-size:1.05rem;font-style:italic;color:rgba(139,111,71,.64);margin-bottom:18px;animation:ivFadeUp 1s ease .14s both;}
    .iv-couple{font-family:'Cormorant SC',serif;font-size:clamp(2.6rem,9.5vw,5.2rem);line-height:.88;letter-spacing:.04em;animation:ivFadeUp 1.2s ease .2s both;}
    .iv-bride{color:var(--ac);}
    .iv-amp{display:block;font-family:'EB Garamond',serif;font-size:.2em;font-style:italic;opacity:.18;margin:11px 0;letter-spacing:.22em;}
    .iv-line{width:52px;height:1px;background:rgba(139,111,71,.18);margin:26px auto;animation:ivFadeUp 1s ease .38s both;}
    .iv-date{font-family:'Cormorant SC',serif;font-size:14px;letter-spacing:.26em;color:var(--txm);animation:ivFadeUp 1s ease .44s both;}
    /* ornament rule */
    .iv-orn{display:flex;align-items:center;gap:10px;justify-content:center;margin-bottom:26px;}
    .iv-orn::before,.iv-orn::after{content:'';flex:1;max-width:58px;height:1px;background:rgba(139,111,71,.12);}
    .iv-orn span{color:rgba(139,111,71,.32);font-size:17px;}
    /* sections */
    .iv-sec{padding:86px 44px;text-align:center;}
    .iv-sec-alt{padding:86px 44px;text-align:center;background:var(--bg2);}
    .iv-inner{max-width:640px;margin:0 auto;}
    .iv-kicker{font-family:'Cormorant SC',serif;font-size:12px;letter-spacing:.52em;color:rgba(139,111,71,.58);margin-bottom:9px;}
    .iv-h2{font-family:'Cormorant SC',serif;font-size:clamp(1.6rem,5vw,2.4rem);margin-bottom:14px;letter-spacing:.04em;}
    /* events */
    .iv-ev-grid{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--bdr);border:1px solid var(--bdr);margin-bottom:22px;}
    .iv-ev{background:var(--bg);padding:26px 18px;}
    .iv-ev-type{font-family:'Cormorant SC',serif;font-size:12px;letter-spacing:.4em;color:var(--ac);margin-bottom:11px;}
    .iv-ev-date{font-size:16px;font-style:italic;margin-bottom:3px;}
    .iv-ev-time{font-family:'Cormorant SC',serif;font-size:13px;letter-spacing:.16em;color:var(--txm);margin-bottom:9px;}
    .iv-ev-venue{font-size:17px;color:var(--txm);line-height:1.88;}
    /* countdown */
    .iv-cd{display:flex;max-width:380px;margin:0 auto;}
    .iv-cd-item{flex:1;border-right:1px solid var(--bdr);padding:14px 6px;}
    .iv-cd-item:last-child{border-right:none;}
    .iv-cd-num{font-family:'Cormorant SC',serif;font-size:clamp(1.9rem,7vw,3rem);color:var(--ac);line-height:1;}
    .iv-cd-unit{font-family:'Cormorant SC',serif;font-size:12px;letter-spacing:.26em;color:var(--txm);margin-top:5px;}
    /* love */
    .iv-love{font-size:16px;line-height:2.25;color:var(--txm);font-style:italic;white-space:pre-line;}
    /* gallery */
    .iv-gal{display:grid;grid-template-columns:1fr 1fr;gap:4px;text-align:left;}
    .iv-gal img{width:100%;aspect-ratio:4/3;object-fit:cover;filter:sepia(.07);cursor:pointer;transition:filter .35s;}
    .iv-gal img:hover{filter:sepia(0);}
    /* rsvp */
    .iv-rsvp-opts{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:13px;}
    .iv-ropt{padding:13px 7px;border:1px solid var(--bdr);cursor:pointer;font-family:'Cormorant SC',serif;font-size:12px;letter-spacing:.14em;color:var(--txm);transition:all .25s;}
    .iv-ropt:hover,.iv-ropt.sel{border-color:var(--ac);color:var(--ac);background:rgba(139,111,71,.04);}
    .iv-inp{width:100%;padding:10px 12px;background:rgba(139,111,71,.03);border:1px solid var(--bdr);color:var(--tx);font-family:'EB Garamond',serif;font-size:16px;font-style:italic;outline:none;resize:none;transition:border-color .25s;margin-bottom:9px;}
    .iv-inp:focus{border-color:var(--ac);}
    .iv-inp::placeholder{color:var(--txm);}
    .iv-btn{padding:11px 28px;background:var(--ac);color:var(--bg);font-family:'Cormorant SC',serif;font-size:13px;letter-spacing:.22em;border:none;cursor:pointer;transition:opacity .2s;}
    .iv-btn:hover{opacity:.85;}.iv-btn:disabled{opacity:.4;cursor:default;}
    .iv-btn-ghost{padding:10px 22px;background:transparent;border:1px solid var(--bdr);color:var(--ac);font-family:'Cormorant SC',serif;font-size:12px;letter-spacing:.18em;cursor:pointer;transition:all .25s;}
    .iv-btn-ghost:hover{background:rgba(139,111,71,.04);}
    /* amplop */
    .iv-amp-card{padding:15px 16px;background:rgba(139,111,71,.03);border:1px solid var(--bdr);display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;}
    .iv-amp-bank{font-family:'Cormorant SC',serif;font-size:12px;letter-spacing:.2em;color:var(--ac);}
    .iv-amp-num{font-size:16px;margin:3px 0;}
    .iv-amp-holder{font-size:16px;color:var(--txm);}
    /* footer */
    .iv-footer{padding:34px;text-align:center;background:var(--bg2);border-top:1px solid var(--bdr);}
    @media(max-width:560px){.iv-ev-grid{grid-template-columns:1fr;}.iv-gal{grid-template-columns:1fr;}.iv-rsvp-opts{grid-template-columns:1fr;}}
  `;

  return (
    <>
      <style>{C}</style>

      {/* ── CLASSICAL HERO ── */}
      <section className="iv-hero">
        <div className="iv-frame" />
        <div className="iv-corner iv-tl">✦</div><div className="iv-corner iv-tr">✦</div>
        <div className="iv-corner iv-bl">✦</div><div className="iv-corner iv-br">✦</div>
        <div className="iv-tag">✦ UNDANGAN PERNIKAHAN ✦</div>
        <div className="iv-ornament">✾</div>
        {guest && <div className="iv-guest">Kepada Yth. {guest.name}</div>}
        <div className="iv-couple">
          {wedding.groom_name}
          <span className="iv-amp">&amp;</span>
          <span className="iv-bride">{wedding.bride_name}</span>
        </div>
        <div className="iv-line" />
        <div className="iv-date">{wc(wedding,'resepsi_date') || wc(wedding,'akad_date')}</div>
      </section>

      {/* ── KATA PEMBUKA ── */}
      {wc(wedding,'opening_text') && (
        <OpeningSection text={wc(wedding,'opening_text')} accentColor="#8B6F47" textColor="rgba(44,36,22,.48)" borderColor="rgba(139,111,71,.12)" bg="rgba(237,229,216,.5)" fontFamily="'EB Garamond',serif" />
      )}
      <MusicPlayer url={wc(wedding,'music_url')} accentColor="#8B6F47" dark={false} />

      {/* ── EVENTS ── */}
      <section className="iv-sec">
        <div className="iv-inner">
          <Rev>
            <div className="iv-kicker">Waktu &amp; Tempat</div>
            <div className="iv-h2">{wedding.groom_name} &amp; {wedding.bride_name}</div>
            <div className="iv-orn"><span>✾</span></div>
            <div className="iv-ev-grid">
              <div className="iv-ev"><div className="iv-ev-type">Akad Nikah</div><div className="iv-ev-date">{wc(wedding,'akad_date')}</div><div className="iv-ev-time">{wc(wedding,'akad_time')}</div><div className="iv-ev-venue">{wc(wedding,'venue_name')}<br />{wc(wedding,'venue_address')}</div></div>
              <div className="iv-ev"><div className="iv-ev-type">Resepsi</div><div className="iv-ev-date">{wc(wedding,'resepsi_date')}</div><div className="iv-ev-time">{wc(wedding,'resepsi_time')}</div><div className="iv-ev-venue">{wc(wedding,'venue_name')}<br />{wc(wedding,'venue_address')}</div></div>
            </div>
            {wc(wedding,'venue_maps_url') && <a href={wc(wedding,'venue_maps_url')} target="_blank" rel="noopener" style={{ display:"inline-flex", alignItems:"center", gap:6, marginBottom:14, padding:"8px 16px", border:"1px solid var(--bdr)", color:"var(--ac)", fontFamily:"'Cormorant SC',serif", fontSize:".43rem", letterSpacing:".18em", textDecoration:"none" }}>📍 Peta Lokasi</a>}
            {wc(wedding,'dress_code') && <div style={{ fontFamily:"'Cormorant SC',serif", fontSize:".46rem", letterSpacing:".2em", color:"var(--txm)" }}>Dress Code · {wc(wedding,'dress_code')}</div>}
            <LiveStreamButton url={wc(wedding,'live_streaming_url')} accentColor="#8B6F47" borderColor="rgba(139,111,71,.18)" />
          </Rev>
        </div>
      </section>

      {/* ── COUNTDOWN ── */}
      <section className="iv-sec-alt">
        <Rev>
          <div className="iv-kicker">Menghitung Hari</div>
          <div className="iv-orn"><span>✾</span></div>
          <div className="iv-cd">
            {[["d","Hari"],["h","Jam"],["m","Menit"],["s","Detik"]].map(([k,l]) => (
              <div key={k} className="iv-cd-item">
                <div className="iv-cd-num">{String(countdown[k]).padStart(2,"0")}</div>
                <div className="iv-cd-unit">{l}</div>
              </div>
            ))}
          </div>
        </Rev>
      </section>

      {/* ── LOVE STORY ── */}
      {wc(wedding,'love_story') && (
        <section className="iv-sec">
          <div className="iv-inner">
            <Rev>
              <div className="iv-kicker">Kisah Kami</div>
              <div className="iv-h2">Perjalanan Cinta</div>
              <div className="iv-orn"><span>✾</span></div>
              <p className="iv-love">{wc(wedding,'love_story')}</p>
            </Rev>
          </div>
        </section>
      )}

      {/* ── GALLERY ── */}
      {photos.length > 0 && (
        <section className="iv-sec-alt">
          <div className="iv-inner" style={{ textAlign:"left" }}>
            <Rev>
              <div style={{ textAlign:"center" }}>
                <div className="iv-kicker">Galeri Foto</div>
                <div className="iv-h2">Momen Berharga</div>
                <div className="iv-orn"><span>✾</span></div>
              </div>
              <div className="iv-gal">
                {photos.map((p,i) => <img key={i} src={p} alt="" onClick={() => setLb(i)} />)}
              </div>
            </Rev>
          </div>
        </section>
      )}

      {/* ── RSVP ── */}
      <section className="iv-sec">
        <div className="iv-inner">
          <Rev>
            <div className="iv-kicker">Konfirmasi Kehadiran</div>
            <div className="iv-h2">RSVP</div>
            <div className="iv-orn"><span>✾</span></div>
            {rsvp.sent ? <div style={{ fontStyle:"italic", color:"rgba(139,111,71,.68)" }}>✦ Terima kasih atas konfirmasimu.</div> : (
              <>
                <div className="iv-rsvp-opts">
                  {[["hadir","✅ Hadir"],["mungkin","🤔 Mungkin"],["tidak_hadir","❌ Tidak Hadir"]].map(([v,l]) => (
                    <div key={v} className={`iv-ropt${rsvp.rsvp===v?" sel":""}`} onClick={() => rsvp.setRsvp(v)}>{l}</div>
                  ))}
                </div>
                <textarea className="iv-inp" rows={3} placeholder="Ucapan..." value={rsvp.msg} onChange={e => rsvp.setMsg(e.target.value)} />
                <button className="iv-btn" onClick={rsvp.submit} disabled={!rsvp.rsvp}>✦ Kirim Konfirmasi</button>
              </>
            )}
          </Rev>
        </div>
      </section>

      {/* ── GUESTBOOK ── */}
      <section className="iv-sec-alt">
        <div className="iv-inner">
          <Rev>
            <div className="iv-kicker">Buku Tamu</div>
            <div className="iv-h2">Ucapan &amp; Doa</div>
            <div className="iv-orn"><span>✾</span></div>
            <LiveFeedTicker weddingId={wedding.id} accentColor="#8B6F47" textColor="rgba(44,36,22,0.6)" bgColor="rgba(139,111,71,0.04)" borderColor="rgba(139,111,71,0.1)" />
            {gb.sent ? <div style={{ fontStyle:"italic", color:"rgba(139,111,71,.58)" }}>✦ Ucapanmu sudah terkirim.</div> : (
              <>
                <input className="iv-inp" placeholder="Nama..." value={gb.name} onChange={e => gb.setName(e.target.value)} />
                <textarea className="iv-inp" rows={3} placeholder="Tulis ucapan..." value={gb.msg} onChange={e => gb.setMsg(e.target.value)} />
                <button className="iv-btn-ghost" onClick={gb.submit}>✦ Kirim Ucapan</button>
              </>
            )}
          </Rev>
        </div>
      </section>

      {/* ── AMPLOP ── */}
      {banks.length > 0 && (
        <section className="iv-sec">
          <div className="iv-inner">
            <Rev>
              <div className="iv-kicker">Amplop Digital</div>
              <div className="iv-h2">Hadiah &amp; Doa</div>
              <div className="iv-orn"><span>✾</span></div>
              {banks.map((b,i) => (
                <div key={i} className="iv-amp-card">
                  <div><div className="iv-amp-bank">{b.bank||b.name}</div><div className="iv-amp-num">{b.number||b.account}</div><div className="iv-amp-holder">a.n. {b.holder}</div></div>
                  {copied===String(i)
                    ? <span style={{ fontFamily:"'Cormorant SC',serif", fontSize:".43rem", color:"var(--ac)" }}>Disalin ✓</span>
                    : <button className="iv-btn-ghost" style={{ padding:"6px 14px" }} onClick={() => copyNum(b.number||b.account, String(i))}>Salin</button>
                  }
                </div>
              ))}
            </Rev>
          </div>
        </section>
      )}

      <div className="iv-footer">
        <div style={{ fontFamily:"'Cormorant SC',serif", fontSize:".5rem", letterSpacing:".4em", color:"rgba(139,111,71,.26)" }}>✦ MYKISAH ✦</div>
        <div style={{ fontSize:".55rem", color:"rgba(44,36,22,.16)", marginTop:4 }}>Dibuat dengan cinta</div>
      </div>

      {lb !== null && <LightboxModal photos={photos} startIndex={lb} onClose={() => setLb(null)} />}
    </>
  );
}
